import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import {
  FiMonitor,
  FiLock,
  FiMail,
  FiCheck,
  FiList,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiStar,
  FiSearch,
  FiUser,
  FiUsers,
  FiMoreHorizontal,
  FiBold,
  FiItalic,
  FiUnderline,
  FiLink,
  FiImage,
  FiAlignLeft,
  FiCreditCard,
  FiChevronRight,
  FiFileText,
  FiDownload,
  FiGift,
  FiX,
} from "react-icons/fi";
import { Spark } from "../components/ui/icons";
import { PROVIDER_BRAND, dark, light, useTheme } from "../theme/ThemeContext";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {
  TextStyle,
  Color,
  FontFamily,
  FontSize,
} from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { TableKit } from "@tiptap/extension-table";
import DOMPurify from "dompurify";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import {
  describeGroupPart,
  describeLedgerGroup,
  describeLedgerRow,
  formatCredits,
  formatCreditsUsd,
  useCredits,
} from "../utils/useCredits";
import {
  getUserSettings,
  invalidateUserSettingsCache,
} from "../lib/userSettingsService";
import { useToast } from "../components/ToastProvider";
import {
  getMailStatus,
  startConnectMailbox,
  unlinkMailbox,
} from "../lib/outreachService";
import Skeleton, { SkeletonText, FadeIn, useFillCount } from "../components/Skeleton";
import DeleteAccount from "../components/DeleteAccount";
import { PageScrollLock } from "../utils/useLockPageScroll";
import { usePlan } from "../utils/usePlan";
import { PlanLockPanel } from "../components/PlanLock";

// ─── Layout shell ─────────────────────────────────────────────
// Break out of MainContent's 2.5rem padding so the rail sits flush
// against the main app sidebar.
// Settings lays out against its own width, not the viewport — the app sidebar can take 18rem of it.
const STACK_AT = "900px";
const PANE_NARROW = "600px";

// Rail stacks above the pane; viewport fallback for browsers without container queries.
const whenStacked = (rules) => css`
  @container settings (max-width: ${STACK_AT}) {
    ${rules}
  }
  @supports not (container-type: inline-size) {
    @media (max-width: ${STACK_AT}) {
      ${rules}
    }
  }
`;

// The content pane itself is phone-width.
const whenPaneNarrow = (rules) => css`
  @container pane (max-width: ${PANE_NARROW}) {
    ${rules}
  }
  @supports not (container-type: inline-size) {
    @media (max-width: ${STACK_AT}) {
      ${rules}
    }
  }
`;

// Mirrors MainContent's padding ladder (App.js) so the page bleeds exactly to its edges.
const Bleed = styled.div`
  container-type: inline-size;
  container-name: settings;
  margin: -2.5rem;
  /* From 1024px down MainContent reserves a top strip for the fixed menu button.
     Bleed past it so the rail's own colour runs up behind the button instead of a
     band of page background; the rail carries the clearance as padding-top. */
  @media (max-width: 1024px) {
    margin: -4.75rem -1.5rem -1.5rem;
  }
  @media (max-width: 768px) {
    margin: -4.25rem -1rem -1rem;
  }
  @media (max-width: 450px) {
    margin: -4rem -0.75rem -0.75rem;
  }
  @media (max-width: 390px) {
    margin: -3.8rem -0.5rem -0.5rem;
  }
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  /* Side by side the shell is exactly one screen tall: the menu stays put and only the panel scrolls. */
  height: 100vh;
  height: 100dvh;
  background: ${({ theme }) => theme.bg};
  ${whenStacked(css`
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
  `)}
`;

const Rail = styled.aside`
  /* --menu-clear follows the viewport (menu button), --rail-pad the layout (stacked or not). */
  --rail-pad: 28px;
  --menu-clear: 0rem;
  background: ${({ theme }) => theme.surface};
  border-right: 1px solid ${({ theme }) => theme.line};
  padding: calc(var(--rail-pad) + var(--menu-clear)) 18px var(--rail-pad);
  overflow-y: auto;
  min-width: 0;
  @media (max-width: 1024px) {
    --menu-clear: 4.75rem;
  }
  @media (max-width: 768px) {
    --menu-clear: 4.25rem;
  }
  @media (max-width: 450px) {
    --menu-clear: 4rem;
  }
  @media (max-width: 390px) {
    --menu-clear: 3.8rem;
  }
  ${whenStacked(css`
    --rail-pad: 16px;
    border-right: 0;
    border-bottom: 1px solid ${({ theme }) => theme.line};
    padding-left: 16px;
    padding-right: 16px;
    overflow: visible;
  `)}
`;

const RailHeader = styled.div`
  h1 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 30px;
    line-height: 1;
    margin: 0;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  div {
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
    margin-top: 4px;
    line-height: 1.4;
  }
  ${whenStacked(css`
    h1 {
      font-size: 26px;
    }
  `)}
`;

// Stacked: the section list becomes one swipeable strip, so the panel stays on screen.
const RailNav = styled.nav`
  ${whenStacked(css`
    display: flex;
    gap: 6px;
    margin: 12px -16px 0;
    padding: 0 16px 2px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  `)}
`;

const RailGroup = styled.div`
  margin-top: 22px;
  ${whenStacked(css`
    display: contents;
  `)}
`;

const RailGroupHeader = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.inkMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  padding: 0 10px 6px;
  ${whenStacked(css`
    display: none;
  `)}
`;

const RailItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === "dark"
        ? theme.surface
        : theme.bg
      : "transparent"};
  color: ${({ $active, theme }) => ($active ? theme.ink : theme.inkMuted)};
  border: 0;
  border-radius: 8px;
  font: inherit;
  font-size: 13.5px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === "dark"
          ? theme.surface
          : theme.bg
        : theme.mode === "dark"
        ? "color-mix(in srgb, var(--ink) 4%, transparent)"
        : "color-mix(in srgb, var(--ink) 4%, transparent)"};
    color: ${({ theme }) => theme.ink};
  }
  ${whenStacked(css`
    flex: 0 0 auto;
    width: auto;
    gap: 7px;
    padding: 7px 12px;
    border: 1px solid
      ${({ $active, theme }) => ($active ? "transparent" : theme.line)};
    border-radius: 999px;
    font-size: 13px;
    white-space: nowrap;
    /* Chips, not 44px slabs from the phone-wide button min-height. */
    min-height: 36px;
    &,
    &:hover {
      background: ${({ $active }) =>
        $active ? "var(--accent-soft)" : "transparent"};
    }
  `)}
`;

const Pane = styled.main`
  container-type: inline-size;
  container-name: pane;
  min-width: 0;
  overflow-y: auto;
  padding: 36px 40px 80px;
  /* cqw here resolves against the settings container, so the gutter eases from tablet to phone. */
  ${whenStacked(css`
    padding: 22px 16px 56px;
    padding: 22px clamp(16px, 4cqw, 40px) 56px;
  `)}
`;

// The panel's contents at 90% on wide screens; the pane itself keeps its full size. Modals and menus portal out, so they're unaffected.
const PaneScale = styled.div`
  @media (min-width: 900px) {
    zoom: 0.9;
  }
`;

// ─── Inline spinner (pending actions) ─────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// ─── Card ─────────────────────────────────────────────────────
const Card = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  overflow: hidden;
`;

const CardHead = styled.div`
  padding: 24px 28px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.line};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  ${whenPaneNarrow(css`
    padding: 18px 16px 16px;
    flex-wrap: wrap;
    gap: 12px;
  `)}
`;

const CardTitle = styled.h2`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 28px;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.ink};
  ${whenPaneNarrow(css`
    font-size: 24px;
  `)}
`;

const CardSub = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.inkMuted};
  margin-top: 4px;
  line-height: 1.5;
  max-width: 600px;
`;

const CardBody = styled.div`
  padding: 22px 28px 28px;
  ${whenPaneNarrow(css`
    padding: 16px;
  `)}
`;

// ─── Row ──────────────────────────────────────────────────────
const Row = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 32px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.line};
  &:first-child {
    padding-top: 4px;
  }
  &:last-child {
    border-bottom: 0;
    padding-bottom: 4px;
  }
  > * {
    min-width: 0;
  }
  ${whenPaneNarrow(css`
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  `)}
`;

const RowLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
`;

const RowHint = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};
  margin-top: 3px;
  line-height: 1.45;
`;

// ─── Segmented picker ─────────────────────────────────────────
const SegWrap = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.bg};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  padding: 3px;
`;

const SegBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: ${({ $active, theme }) => ($active ? theme.surface : "transparent")};
  color: ${({ $active, theme }) => ($active ? theme.ink : theme.inkMuted)};
  border: 0;
  border-radius: 7px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  box-shadow: ${({ $active }) =>
    $active ? "var(--shadow-sm)" : "none"};
  &:hover {
    color: ${({ theme }) => theme.ink};
  }
`;

const Swatch = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.line};
  ${({ $kind }) =>
    $kind === "light"
      ? css`
          background: var(--on-fill);
        `
      : $kind === "dark"
      ? css`
          background: ${dark.bg};
          border-color: ${dark.line};
        `
      : css`
          background: linear-gradient(
            135deg,
            ${light.surface} 0% 50%,
            ${dark.bg} 50% 100%
          );
        `}
`;

// ─── Inputs / buttons ─────────────────────────────────────────
// `font: inherit` MUST come before font-size — it's a shorthand, so declaring
// it after silently resets the size back to the inherited 16px. That's what
// made every settings field (and the prompt textarea especially) render
// oversized despite the 13.5px below.
const TextInput = styled.input`
  width: 100%;
  /* No global box-sizing reset in this app (App.js sets it on html/body only),
     so width:100% + padding + border overflows the parent — inside ModalCard
     that overflow is clipped and the field looks flush with the right edge. */
  box-sizing: border-box;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13.5px;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  &::placeholder {
    color: ${({ theme }) => theme.inkMuted};
    opacity: 0.7;
  }
  &:focus {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => `0 0 0 3px ${theme.accent}22`};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box; /* see TextInput — no global reset in this app */
  display: block; /* kill the inline-block baseline gap under the field */
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13.5px;
  /* Prose, not a single-line value — needs room to breathe between lines. */
  line-height: 1.6;
  outline: none;
  resize: vertical;
  min-height: 96px;
  transition: border-color 0.12s, box-shadow 0.12s;
  &::placeholder {
    color: ${({ theme }) => theme.inkMuted};
    opacity: 0.7;
  }
  &:focus {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => `0 0 0 3px ${theme.accent}22`};
  }
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${({ $sm }) => ($sm ? "0 10px" : "0 14px")};
  height: ${({ $sm }) => ($sm ? "30px" : "34px")};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.ink};
  font-family: "Geist", ui-sans-serif, system-ui, sans-serif;
  font-size: ${({ $sm }) => ($sm ? "12px" : "13px")};
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: ${({ theme }) => theme.bg};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled(Btn)`
  background: ${({ theme }) => theme.accent};
  border-color: ${({ theme }) => theme.accent};
  color: var(--on-fill);
  &:hover {
    background: ${({ theme }) => theme.accent};
    filter: brightness(1.08);
  }
`;

const DangerLink = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--danger);
  background: var(--danger-soft);
  color: var(--danger);
  font-family: "Geist", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: var(--danger);
    border-color: var(--danger);
    color: var(--on-fill);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// ─── Mailbox row ──────────────────────────────────────────────
const MbRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.surface};
  /* Buttons drop under the mailbox when the two no longer fit side by side. */
  flex-wrap: wrap;
  &:last-child {
    margin-bottom: 0;
  }
`;

const MbLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1 1 220px;
  min-width: 0;
  > div:last-child {
    min-width: 0;
  }
`;

const MbLogo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
  background: var(--on-fill);
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ $color }) => $color || "var(--ink)"};
`;

const MbName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.2;
`;

const MbSub = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ConnectedDot = styled.span`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--success);
  display: inline-block;
`;

// ─── Checklist ────────────────────────────────────────────────
const ChToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 8px;
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.inkMuted};
  svg {
    flex-shrink: 0;
  }
  input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    outline: none;
    color: ${({ theme }) => theme.ink};
    font: inherit;
    font-size: 13.5px;
  }
`;

const ChCount = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.inkMuted};
  white-space: nowrap;
  b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

const ChGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
  gap: 4px;
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  padding: 6px;
  background: ${({ theme }) => theme.bg};
`;

const ChItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 7px;
  min-width: 0; /* lets ChName ellipsize instead of widening the grid track */
  cursor: pointer;
  transition: background 0.1s;
  background: ${({ $on, theme }) =>
    $on ? "var(--accent-soft)" : "transparent"};
  &:hover {
    background: ${({ $on, theme }) =>
      $on
        ? "var(--accent-soft)"
        : "color-mix(in srgb, var(--ink) 4%, transparent)"};
  }
  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const ChCheck = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid
    ${({ $on, theme }) => ($on ? theme.accent : theme.line)};
  background: ${({ $on, theme }) => ($on ? theme.accent : theme.surface)};
  color: var(--on-fill);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: background 0.1s, border-color 0.1s;
`;

const ChName = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SaveBar = styled.div`
  margin-top: 14px;
  padding: 10px 14px;
  background: ${({ theme }) =>
    theme.mode === "dark" ? theme.surface : theme.bg};
  color: ${({ theme }) => theme.ink};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
`;

// ─── Prompt / signature edit ─────────────────────────────────
const EditTitle = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 20px;
  color: ${({ theme }) => theme.ink};
  margin-bottom: 14px;
`;

const Field = styled.div`
  margin-bottom: 14px;
  label {
    display: block;
    font-size: 12.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
    margin-bottom: 5px;
  }
`;

const FieldHint = styled.div`
  font-size: 11.5px;
  color: ${({ theme }) => theme.inkMuted};
  margin-top: 5px;
  line-height: 1.4;
  code {
    font-size: 11px;
    background: ${({ theme }) => theme.surface};
    padding: 1px 5px;
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.line};
  }
`;

const EditFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

// ─── Modal ────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: var(--scrim);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  @media (max-width: 600px), (max-height: 600px) {
    padding: 12px;
  }
`;

// $sm: short forms (a name + a textarea) that shouldn't be forced into the
// 640px frame the rich signature editor needs.
const ModalCard = styled.div`
  width: 100%;
  /* border-box so width:100% can't overflow a phone; sizes include the padding. */
  box-sizing: border-box;
  max-width: ${({ $sm }) => ($sm ? "612px" : "912px")};
  height: ${({ $sm }) => ($sm ? "auto" : "690px")};
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 16px;
  padding: 24px 26px;
  box-shadow: var(--shadow-lg);
  /* Scroll rather than clip: on a short screen the footer buttons must stay reachable. */
  overflow-x: hidden;
  overflow-y: auto;
  @media (max-width: 600px), (max-height: 600px) {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    padding: 18px 16px;
  }
`;

// ─── Prompt list ──────────────────────────────────────────────
const PromptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PromptRow = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr auto auto auto;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
  ${whenPaneNarrow(css`
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    gap: 8px 4px;
    padding: 12px;
    > :nth-child(1) {
      display: none;
    }
    > :nth-child(2) {
      grid-column: 1 / -1;
    }
    > :nth-child(3) {
      grid-column-start: 2;
    }
  `)}
`;

const PromptIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--accent-soft);
  color: ${({ theme }) => theme.accent};
  display: grid;
  place-items: center;
  margin-top: 2px;
`;

const PromptName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  line-height: 1.3;
  flex-wrap: wrap;
  overflow-wrap: anywhere;
`;

const PromptBody = styled.div`
  margin-top: 4px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid transparent;
  color: ${({ $danger, theme }) => ($danger ? "var(--danger)" : "var(--ink-muted)")};
  cursor: pointer;
  display: grid;
  place-items: center;
  &:hover {
    background: ${({ theme }) => theme.bg};
    border-color: ${({ theme }) => theme.line};
    color: ${({ $danger, theme }) => ($danger ? "var(--danger)" : "var(--ink)")};
  }
  /* Square, not 30×44 from the phone-wide button min-height. */
  @media (max-width: 450px) {
    width: 36px;
    height: 36px;
    min-height: 36px;
  }
`;

// ─── Signature card ───────────────────────────────────────────
const SigList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SigCard = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  cursor: pointer;
  transition: border-color 0.12s;
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
`;

const SigHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 15px;
  background: ${({ theme }) => theme.surface};
  flex-wrap: wrap;
`;

const SigName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const SigTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--success-soft);
  color: var(--success);
  font-size: 11px;
  font-weight: 600;
`;

// ─── Empty block ──────────────────────────────────────────────
const EmptyBlock = styled.div`
  padding: 40px 24px;
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.line};
  border-radius: 12px;
  background: ${({ theme }) => theme.bg};
`;

/* Card-level size (54/15) of the shared empty-state design. */
const EmptyIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 15px;
  background: ${({ theme }) =>
    `linear-gradient(155deg, ${theme.accent}1f, ${theme.surface})`};
  color: ${({ theme }) => theme.accent};
  display: grid;
  place-items: center;
  margin: 0 auto 14px;
  border: 1px solid ${({ theme }) => theme.line};
  box-shadow: ${({ theme }) => `0 6px 16px -8px ${theme.accent}59`};
`;

const EmptyTitle = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 20px;
  line-height: 1.15;
  letter-spacing: -0.005em;
  color: ${({ theme }) => theme.ink};
  margin-bottom: 6px;
`;

const EmptySub = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.inkMuted};
  margin-bottom: 18px;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

// ─── Nav config ───────────────────────────────────────────────
const NAV = [
  { id: "profile", label: "Profile", icon: FiUser, group: "Workspace" },
  { id: "team", label: "Team", icon: FiUsers, group: "Workspace" },
  { id: "appearance", label: "Appearance", icon: FiMonitor, group: "Workspace" },
  { id: "mailbox", label: "Mailbox Connection", icon: FiMail, group: "Workspace", feature: "outreach" },
  { id: "email-list", label: "Email alerts", icon: FiList, group: "Sourcing", feature: "emailAlerts" },
  { id: "prompts", label: "AI Prompts", icon: Spark, group: "Email", feature: "outreach" },
  { id: "signatures", label: "Signatures", icon: FiEdit2, group: "Email", feature: "outreach" },
  { id: "credits", label: "Credits & Usage", icon: FiCreditCard, group: "Billing" },
  { id: "referrals", label: "Refer a friend", icon: FiGift, group: "Billing" },
  { id: "billing", label: "Billing & Invoices", icon: FiFileText, group: "Billing" },
];

// ─── Shared card wrapper ──────────────────────────────────────
function SettingsCard({ title, sub, actions, children, tour }) {
  return (
    <Card data-tour={tour}>
      <CardHead>
        <div>
          <CardTitle>{title}</CardTitle>
          <CardSub>{sub}</CardSub>
        </div>
        {actions && <div>{actions}</div>}
      </CardHead>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function SettingRow({ label, hint, children, tour }) {
  return (
    <Row data-tour={tour}>
      <div>
        <RowLabel>{label}</RowLabel>
        {hint && <RowHint>{hint}</RowHint>}
      </div>
      <div>{children}</div>
    </Row>
  );
}

// ─── Credits panel ────────────────────────────────────────────
const BalanceFigure = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 40px;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.ink};
  display: flex;
  align-items: baseline;
  gap: 10px;

  span {
    font-family: inherit;
    font-size: 16px;
    color: ${({ theme }) => theme.inkMuted};
    letter-spacing: 0;
  }
`;

// Approximate dollar value: a quiet aside next to a credit figure, never competing with it.
const UsdNote = styled.span`
  font-family: "Geist", ui-sans-serif, system-ui, sans-serif;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.inkMuted};
  opacity: 0.75;
  white-space: nowrap;
  ${({ $block }) => ($block ? "display: block; margin-top: 8px; text-align: inherit;" : "margin-left: 8px;")}
`;

const UsageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  line-height: 20px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
    padding: 0 24px 10px 0;
    border-bottom: 1px solid ${({ theme }) => theme.line};
    white-space: nowrap;
  }

  td {
    padding: 11px 24px 11px 0;
    border-bottom: 1px solid ${({ theme }) => theme.line};
    color: ${({ theme }) => theme.ink};
    vertical-align: top;
    white-space: nowrap;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  /* When, Activity and Credits hug their text; Reference takes the rest and truncates on one line. */
  th:nth-child(1),
  th:nth-child(2),
  th:nth-child(4) {
    width: 1%;
  }
  td:nth-child(3) {
    width: 100%;
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* The expand arrow hangs in a fixed gutter so every activity label starts on the same line. */
  th:nth-child(2),
  td:nth-child(2) {
    padding-left: 20px;
  }
  td:nth-child(2) {
    position: relative;
  }
  .chev {
    position: absolute;
    left: 0;
    top: 14px;
    color: ${({ theme }) => theme.inkMuted};
    transition: transform 0.15s ease;
  }
  .chev.open {
    transform: rotate(90deg);
  }

  tr.expandable {
    cursor: pointer;
  }
  tr.expandable:hover td {
    background: ${({ theme }) => theme.hover || "transparent"};
  }
  tr.sub td {
    padding-top: 7px;
    padding-bottom: 7px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
    border-bottom-style: dashed;
  }
  tr.sub td:nth-child(2) {
    padding-left: 34px;
  }

  td.amount,
  th.amount {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  td.muted {
    color: ${({ theme }) => theme.inkMuted};
  }

  tr:last-child td {
    border-bottom: 0;
  }

  /* Four columns don't fit a phone: each entry becomes activity + credits, then reference, then time. */
  ${whenPaneNarrow(css`
    display: block;
    thead {
      display: none;
    }
    tbody {
      display: block;
    }
    tr {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 1px 12px;
      padding: 11px 0 11px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.line};
    }
    tr:last-child {
      border-bottom: 0;
    }
    tr.sub {
      padding: 8px 0 8px 34px;
      border-bottom-style: dashed;
    }
    .chev {
      top: 14px;
    }
    td,
    tr.sub td {
      display: block;
      padding: 0;
      border-bottom: 0;
    }
    td:nth-child(1) {
      grid-area: 3 / 1 / 4 / 3;
      font-size: 12px;
    }
    /* Same selectors as the table rules above, or those would out-rank a bare td here. */
    td:nth-child(2),
    tr.sub td:nth-child(2) {
      grid-area: 1 / 1;
      position: static; /* the arrow anchors to the row's gutter instead */
      padding-left: 0;
      white-space: normal;
    }
    td:nth-child(3) {
      grid-area: 2 / 1 / 3 / 3;
      width: auto;
      max-width: none;
      white-space: normal;
      overflow-wrap: anywhere;
    }
    td:nth-child(4) {
      grid-area: 1 / 2;
    }
    td:empty {
      display: none;
    }

    /* Team tables have different columns than the usage grid above was mapped for; the person
       leads with the actions beside it, then the rest flows on one wrapped line. */
    &.seats tr,
    &.people tr {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 10px;
      padding-left: 0;
    }
    &.seats td,
    &.people td {
      width: auto;
      max-width: none;
      font-size: 12.5px;
    }
    &.seats td:nth-child(2),
    &.people td:nth-child(1) {
      order: -2;
      flex: 1 1 calc(100% - 60px);
      min-width: 0;
      font-size: 13.5px;
      white-space: normal;
      overflow-wrap: anywhere;
    }
    &.seats td:nth-child(6),
    &.people td:nth-child(4) {
      order: -1;
      margin-left: auto;
    }
    &.people td:nth-child(4) {
      order: 1;
      flex: 1 1 100%;
    }
    &.seats td[colspan] {
      flex: 1 1 100%;
    }
  `)}
`;

const UsageFoot = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 14px;
`;

const GhostButton = styled.button`
  appearance: none;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ theme }) => theme.ink};
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ─── Billing panel ────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InlineForm = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
  max-width: 440px;
  ${whenPaneNarrow(css`
    flex-direction: column;
    align-items: stretch;
  `)}
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.accent};
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(Number(n) || 0);

function describeInvoice(inv) {
  const first = inv.lines?.[0];
  // Only the Oct 2026 test credit notes, from before upgrades stopped prorating; no new ones are recorded.
  if (inv.kind === "credit_note") return `Credit note, ${money(inv.refund ?? -inv.total, inv.currency)} refunded`;
  if (inv.kind === "plan_change") return first?.description || "Plan upgrade";
  if (!first) return inv.kind === "topup" ? "Extra credits" : "Plan";
  const main = first.description.replace(/, per seat$/, "");
  const extra = (inv.lines?.length || 1) - 1;
  return extra > 0 ? `${main}, plus ${extra} more item${extra === 1 ? "" : "s"}` : main;
}

function BillingPanel() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [managedBy, setManagedBy] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/billing/status")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => alive && body?.role === "member" && setManagedBy(body.managedBy || "your account admin"))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const [contact, setContact] = React.useState("");
  const [savedContact, setSavedContact] = React.useState("");
  const [contactLoading, setContactLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [invoices, setInvoices] = React.useState([]);
  const [invoicesLoading, setInvoicesLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/billing/contact")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!alive) return;
        const email = body?.email || user?.email || "";
        setContact(email);
        setSavedContact(email);
      })
      .catch(() => {})
      .finally(() => alive && setContactLoading(false));
    fetchWithAuth("/api/billing/invoices")
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || "We couldn't load your invoices just now.");
        if (alive) setInvoices(Array.isArray(body.invoices) ? body.invoices : []);
      })
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setInvoicesLoading(false));
    return () => {
      alive = false;
    };
  }, [user?.email]);

  // Fetch the PDF in-page and save it from a blob: a plain `<a download>` goes through the
  // browser's own download path, which fails on the dev box's self-signed certificate.
  const downloadInvoice = async (inv) => {
    const res = await fetchWithAuth(`/api/billing/invoices/${encodeURIComponent(inv.id)}/pdf`);
    if (!res.ok) throw new Error("download failed");
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `scoutm8-invoice-${inv.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const saveContact = async (e) => {
    e.preventDefault();
    const email = contact.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return showToast("Please enter a valid email for invoices.");
    setSaving(true);
    try {
      const res = await fetchWithAuth("/api/billing/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't save your billing email just now.");
      setSavedContact(body.email || email);
      setContact(body.email || email);
      showToast(`Invoices will go to ${body.email || email}`);
    } catch (err) {
      showToast(err.message || "We couldn't save your billing email just now.");
    } finally {
      setSaving(false);
    }
  };

  const when = (at) => new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const dirty = contact.trim().toLowerCase() !== savedContact;

  return (
    <>
      {managedBy ? (
        <SettingsCard title="Billing & Invoices" sub="Billing for this workspace is handled by your account admin.">
          <SettingRow label="Account admin" hint="Invoices and the billing email live on their account.">
            <span>{managedBy}</span>
          </SettingRow>
        </SettingsCard>
      ) : (
      <>
      <SettingsCard title="Billing" sub="Each invoice is emailed here as soon as a payment goes through, as a PDF.">
        <SettingRow label="Send invoices to" hint="Card details are held by our payment provider, not by scoutm8.">
          {contactLoading ? (
            <Skeleton width={220} height={36} />
          ) : (
            <InlineForm onSubmit={saveContact}>
              <TextInput
                type="email"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="billing@yourcompany.com"
                autoComplete="email"
              />
              <GhostButton type="submit" disabled={saving || !dirty}>
                {saving ? "Saving…" : "Save"}
              </GhostButton>
            </InlineForm>
          )}
        </SettingRow>
      </SettingsCard>
      <div style={{ height: 18 }} />
      <SettingsCard title="Invoices" sub="One per payment on your account, newest first. Download any of them as a PDF.">
        {error && <RowHint style={{ marginBottom: 12 }}>{error}</RowHint>}
        {!invoicesLoading && !error && invoices.length === 0 ? (
          <RowHint>No invoices yet. Your first one appears here once a plan or extra credits are paid for.</RowHint>
        ) : (
          <UsageTable>
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>For</th>
                <th className="amount">Amount</th>
                <th className="amount">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoicesLoading && invoices.length === 0
                ? Array.from({ length: 3 }).map((_, i) => (
                    <tr key={`sk-${i}`} aria-hidden="true">
                      <td><Skeleton width={96} height={13} /></td>
                      <td><Skeleton width={80} height={13} /></td>
                      <td><Skeleton width={`${50 + ((i * 17) % 30)}%`} height={13} /></td>
                      <td className="amount"><Skeleton width={56} height={13} /></td>
                      <td className="amount"><Skeleton width={70} height={13} /></td>
                    </tr>
                  ))
                : invoices.map((inv) => (
                    <FadeIn as="tr" key={inv.id}>
                      <td className="muted">{when(inv.paidAt)}</td>
                      <td>{inv.number}</td>
                      <td className="muted" title={inv.lines?.map((l) => `${l.description} × ${l.quantity}`).join("\n")}>
                        {describeInvoice(inv)}
                      </td>
                      <td className="amount">{money(inv.total, inv.currency)}</td>
                      <td className="amount">
                        <DownloadLink
                          href={`/api/billing/invoices/${encodeURIComponent(inv.id)}/pdf`}
                          onClick={(e) => {
                            e.preventDefault();
                            downloadInvoice(inv).catch(() => showToast("We couldn't download that invoice just now."));
                          }}
                        >
                          <FiDownload size={14} aria-hidden="true" /> Download
                        </DownloadLink>
                      </td>
                    </FadeIn>
                  ))}
            </tbody>
          </UsageTable>
        )}
      </SettingsCard>
      </>
      )}
    </>
  );
}

// ─── Cancel plan dialog ───────────────────────────────────────
const CANCEL_REASONS = [
  ["too_expensive", "It costs too much"],
  ["not_using", "I'm not using it enough"],
  ["missing_feature", "It's missing something I need"],
  ["switching", "I'm moving to another tool"],
  ["pausing", "Just taking a break"],
  ["other", "Something else"],
];

const CancelFacts = styled.ul`
  list-style: none;
  margin: 0 0 18px;
  padding: 16px 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bg};
  border: 1px solid ${({ theme }) => theme.line};
  display: grid;
  gap: 10px;
  li {
    display: grid;
    grid-template-columns: 18px 1fr;
    gap: 10px;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.ink};
  }
  li svg {
    margin-top: 2px;
    color: ${({ theme }) => theme.accent};
  }
  li b {
    font-weight: 600;
  }
`;

const ReasonList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 14px;
  ${whenPaneNarrow(css`
    grid-template-columns: 1fr;
  `)}
  /* Modals portal to <body>, outside the pane container, so the container query never fires there. */
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ReasonChip = styled.button`
  appearance: none;
  text-align: left;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.accent : theme.line)};
  background: ${({ theme, $on }) => ($on ? theme.accentSoft : theme.surface)};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
`;

const DangerOutlineButton = styled(GhostButton)`
  border-color: var(--danger);
  color: var(--danger);
  &:hover {
    background: var(--danger-soft);
  }
`;


const DangerButton = styled(GhostButton)`
  border-color: var(--danger);
  background: var(--danger);
  color: var(--on-fill);
  &:hover {
    opacity: 0.92;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
  ${whenPaneNarrow(css`
    flex-direction: column-reverse;
    button {
      width: 100%;
    }
  `)}
  /* Only ever rendered in portalled modals, where the pane container query can't reach. */
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    button {
      width: 100%;
    }
  }
`;

function CancelPlanModal({ plan, onClose, onCancelled }) {
  const { showToast } = useToast();
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const endDate = plan?.renewsAt ? new Date(plan.renewsAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "your renewal date";
  const yearly = plan?.cycle === "yearly";

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth("/api/billing/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason || null, note }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't cancel the plan just now.");
      onCancelled(body);
      showToast(`Plan ends on ${endDate}`);
    } catch (err) {
      showToast(err.message || "We couldn't cancel the plan just now.");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <ModalOverlay onMouseDown={() => !busy && onClose()}>
      <PageScrollLock />
      <ModalCard $sm onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cancel-plan-title">
        <EditTitle id="cancel-plan-title" style={{ fontSize: 24 }}>
          Cancel your {plan?.planName || ""} plan?
        </EditTitle>
        <CancelFacts>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Everything stays on until {endDate}.</b> That's the end of the {yearly ? "year" : "month"} you've already paid for.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Nothing more is charged</b>, and nothing is refunded.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Your credits stay usable</b> until then. After that, new searches and lookups pause until you pick a plan again.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Changed your mind?</b> "Keep current plan" undoes this any time before {endDate}.
            </span>
          </li>
        </CancelFacts>
        <RowLabel style={{ marginBottom: 8 }}>Mind telling us why? <span style={{ fontWeight: 400, color: "var(--ink-muted)" }}>Optional</span></RowLabel>
        <ReasonList>
          {CANCEL_REASONS.map(([id, label]) => (
            <ReasonChip key={id} type="button" $on={reason === id} onClick={() => setReason(reason === id ? "" : id)} aria-pressed={reason === id}>
              {label}
            </ReasonChip>
          ))}
        </ReasonList>
        <TextArea
          rows={3}
          placeholder="Anything we could have done better?"
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <ModalActions>
          <GhostButton type="button" onClick={onClose} disabled={busy}>
            Keep my plan
          </GhostButton>
          <DangerButton type="button" onClick={confirm} disabled={busy}>
            {busy ? "Cancelling…" : `Cancel on ${endDate}`}
          </DangerButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>,
    document.body,
  );
}

// ─── Team panel ─────────────────────────────────────────
const RolePill = styled.span`
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid ${({ theme, $tone }) => ($tone === "admin" ? theme.accent : theme.line)};
  color: ${({ theme, $tone }) => ($tone === "admin" ? theme.accent : $tone === "pending" ? theme.inkMuted : theme.ink)};
`;

const TableActions = styled.div`
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  button {
    appearance: none;
    background: none;
    border: 0;
    padding: 2px 4px;
    font: inherit;
    font-size: 12.5px;
    color: ${({ theme }) => theme.accent};
    cursor: pointer;
    &:disabled {
      color: ${({ theme }) => theme.inkMuted};
      cursor: default;
    }
    &.danger {
      color: var(--danger);
    }
  }
`;

const EmptySeatBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border: 1.5px dashed ${({ theme }) => theme.line};
  border-radius: 10px;
  color: ${({ theme }) => theme.inkMuted};
  font-size: 13px;
  .label {
    flex: 1;
    min-width: 0;
  }
  .label b {
    color: ${({ theme }) => theme.ink};
    font-weight: 500;
  }
  /* Side by side the invite form squeezed to a sliver beside the label. */
  ${whenPaneNarrow(css`
    flex-wrap: wrap;
    form {
      flex: 1 1 100%;
    }
  `)}
`;

function RemoveMemberModal({ member, onClose, onRemoved }) {
  const { showToast } = useToast();
  const [busy, setBusy] = React.useState(false);
  const who = member?.name || member?.email || "this person";
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(`/api/team/members/${encodeURIComponent(member.userId)}`, { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't remove that person just now.");
      onRemoved(body);
      showToast(`${who} was removed`);
    } catch (err) {
      showToast(err.message || "We couldn't remove that person just now.");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <ModalOverlay onMouseDown={() => !busy && onClose()}>
      <PageScrollLock />
      <ModalCard $sm onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="remove-member-title">
        <EditTitle id="remove-member-title" style={{ fontSize: 24 }}>
          Remove {who}?
        </EditTitle>
        <CancelFacts>
          <li>
            <FiCheck size={14} />
            <span>
              <b>They lose access straight away.</b> Their seat opens up for someone else.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Their remaining credits stay with them</b> and are not moved to your balance.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Searches and shortlists they made</b> stay in the workspace.
            </span>
          </li>
        </CancelFacts>
        <ModalActions>
          <GhostButton type="button" onClick={onClose} disabled={busy}>
            Keep them
          </GhostButton>
          <DangerButton type="button" onClick={confirm} disabled={busy}>
            {busy ? "Removing…" : "Remove"}
          </DangerButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>,
    document.body,
  );
}

const MenuWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuTrigger = styled.button`
  appearance: none;
  border: 1px solid transparent;
  background: none;
  color: ${({ theme }) => theme.inkMuted};
  width: 30px;
  height: 28px;
  border-radius: 8px;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  &:hover,
  &[aria-expanded="true"] {
    border-color: ${({ theme }) => theme.line};
    color: ${({ theme }) => theme.ink};
    background: ${({ theme }) => theme.bg};
  }
  @media (max-width: 450px) {
    width: 36px;
    height: 36px;
    min-height: 36px;
  }
`;

const MenuList = styled.div`
  position: fixed;
  z-index: 1200;
  min-width: 190px;
  padding: 6px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 12px 32px rgba(6, 32, 61, 0.14);
  text-align: left;
  .group {
    padding: 6px 10px 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.inkMuted};
  }
  .sep {
    height: 1px;
    margin: 6px 4px;
    background: ${({ theme }) => theme.line};
  }
  button {
    appearance: none;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border: 0;
    background: none;
    border-radius: 8px;
    padding: 7px 10px;
    font: inherit;
    font-size: 13px;
    color: ${({ theme }) => theme.ink};
    cursor: pointer;
    text-align: left;
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.bg};
    }
    &:disabled {
      color: ${({ theme }) => theme.inkMuted};
      cursor: default;
    }
    &.danger {
      color: var(--danger);
    }
    small {
      color: ${({ theme }) => theme.inkMuted};
      font-size: 11.5px;
    }
  }
`;

// The ⋯ menu on a colleague's row: change plan, make admin, remove.
function RowMenu({ label, children }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const ref = React.useRef(null);
  const menuRef = React.useRef(null);
  // The list is portalled to the body and placed from the button, so the card's overflow
  // can't clip it; it closes on outside click, Escape, scroll or resize.
  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  };
  React.useEffect(() => {
    if (!open) return undefined;
    place();
    const close = (e) => {
      if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    const away = () => setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", away, true);
    window.addEventListener("resize", away);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", away, true);
      window.removeEventListener("resize", away);
    };
  }, [open]);
  return (
    <MenuWrap ref={ref}>
      <MenuTrigger type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((v) => !v)}>
        <FiMoreHorizontal size={16} />
      </MenuTrigger>
      {open &&
        pos &&
        createPortal(
          <MenuList ref={menuRef} role="menu" style={{ top: pos.top, right: pos.right }} onClick={() => setOpen(false)}>
            {children}
          </MenuList>,
          document.body,
        )}
    </MenuWrap>
  );
}

function MakeAdminModal({ member, onClose, onDone }) {
  const { showToast } = useToast();
  const [busy, setBusy] = React.useState(false);
  const who = member?.name || member?.email || "this person";
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth("/api/team/owner", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: member.userId }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't hand over the admin role just now.");
      onDone(body);
      showToast(`${who} is now the admin`);
    } catch (err) {
      showToast(err.message || "We couldn't hand over the admin role just now.");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <ModalOverlay onMouseDown={() => !busy && onClose()}>
      <PageScrollLock />
      <ModalCard $sm onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="make-admin-title">
        <EditTitle id="make-admin-title" style={{ fontSize: 24 }}>
          Make {who} the admin?
        </EditTitle>
        <CancelFacts>
          <li>
            <FiCheck size={14} />
            <span>
              <b>They take over billing.</b> The plan, the saved card and the invoices move to their account.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>You become a member.</b> You keep your seat, your plan and your credits, but can no longer change the plan or the team.
            </span>
          </li>
          <li>
            <FiCheck size={14} />
            <span>
              <b>Only they can hand it back.</b>
            </span>
          </li>
        </CancelFacts>
        <ModalActions>
          <GhostButton type="button" onClick={onClose} disabled={busy}>
            Keep it
          </GhostButton>
          <DangerButton type="button" onClick={confirm} disabled={busy}>
            {busy ? "Handing over…" : "Make admin"}
          </DangerButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>,
    document.body,
  );
}

function TeamPanel() {
  const { showToast } = useToast();
  const [team, setTeam] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [inviting, setInviting] = React.useState(null); // seatId with the invite form open
  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [busyId, setBusyId] = React.useState("");
  const [removing, setRemoving] = React.useState(null);
  const [promoting, setPromoting] = React.useState(null);
  const [seatFor, setSeatFor] = React.useState({}); // memberUserId -> chosen seatId

  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/team")
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || "We couldn't load your team just now.");
        if (alive) setTeam(body);
      })
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const owner = team?.role === "owner";
  const paid = Boolean(team?.paid);
  const seatList = team?.seatList || [];
  const openSeats = team?.openSeats || [];
  const members = team?.members || [];
  const invites = team?.invites || [];
  const filled = seatList.filter((x) => x.userId).length;
  const unseated = members.filter((m) => m.role !== "owner" && !m.seatId);
  const tierSummary = seatList.reduce((acc, x) => ({ ...acc, [x.planName]: (acc[x.planName] || 0) + 1 }), {});
  const tierLabel = Object.entries(tierSummary).map(([name, n]) => `${n}× ${name}`).join(", ");
  const when = (at) => {
    const d = new Date(at);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };
  const call = async (url, init, okText) => {
    const res = await fetchWithAuth(url, init);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error || "That didn't work just now.");
    setTeam(body);
    if (okText) showToast(okText);
    return body;
  };

  const invite = async (e, seatId) => {
    e.preventDefault();
    if (sending || !email.trim()) return;
    setSending(true);
    try {
      await call(
        "/api/team/invites",
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim(), seatId }) },
        `Invite sent to ${email.trim()}`,
      );
      setEmail("");
      setInviting(null);
    } catch (err) {
      showToast(err.message);
    } finally {
      setSending(false);
    }
  };
  const inviteAction = async (inv, action) => {
    if (busyId) return;
    setBusyId(inv.id);
    try {
      const url = action === "resend" ? `/api/team/invites/${encodeURIComponent(inv.id)}/resend` : `/api/team/invites/${encodeURIComponent(inv.id)}`;
      await call(url, { method: action === "resend" ? "POST" : "DELETE" }, action === "resend" ? `Invite resent to ${inv.email}` : `Invite to ${inv.email} cancelled`);
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusyId("");
    }
  };
  const assign = async (m) => {
    const seatId = seatFor[m.userId] || openSeats[0]?.id;
    if (!seatId || busyId) return;
    setBusyId(m.userId);
    try {
      await call(
        `/api/team/seats/${encodeURIComponent(seatId)}/assign`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: m.userId }) },
        `${m.name || m.email} now has a seat`,
      );
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusyId("");
    }
  };

  const personCell = (m, you) => (
    <>
      <div>
        {m.name || m.email || m.userId}
        {you ? " (you)" : ""}
      </div>
      {m.name && m.email && <RowHint style={{ marginTop: 1 }}>{m.email}</RowHint>}
    </>
  );

  const seatRow = (x, i) => {
    const holder = x.holder ? members.find((m) => m.userId === x.userId) || x.holder : null;
    const isOwnerSeat = x.userId && x.userId === team?.ownerUserId;
    const inv = x.invite ? invites.find((v) => v.id === x.invite.id) || x.invite : null;
    const formOpen = inviting === x.id;
    if (!holder && !inv) {
      // An empty seat is one plain box: seat, plan, and the invite.
      return (
        <FadeIn as="tr" key={x.id}>
          <td colSpan={owner ? 6 : 5} style={{ paddingLeft: 0, paddingRight: 0 }}>
            <EmptySeatBox>
              <span className="label">
                <b>Seat {i + 1}</b> · {x.planName} seat, empty
              </span>
              {owner && formOpen && (
                <InlineForm onSubmit={(e) => invite(e, x.id)} style={{ maxWidth: 380 }}>
                  <TextInput type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@yourfirm.com" autoComplete="off" disabled={sending} />
                  <PrimaryBtn type="submit" disabled={sending || !email.trim()}>
                    {sending ? "Sending…" : "Send"}
                  </PrimaryBtn>
                  <GhostButton type="button" onClick={() => setInviting(null)} disabled={sending}>
                    Cancel
                  </GhostButton>
                </InlineForm>
              )}
              {owner && !formOpen && (
                <GhostButton
                  type="button"
                  onClick={() => {
                    setEmail("");
                    setInviting(x.id);
                  }}
                >
                  Invite
                </GhostButton>
              )}
            </EmptySeatBox>
          </td>
        </FadeIn>
      );
    }
    return (
      <FadeIn as="tr" key={x.id}>
        <td className="muted">Seat {i + 1}</td>
        <td>
          {holder ? (
            personCell(holder, isOwnerSeat && owner)
          ) : inv ? (
            <>
              <div>{inv.email}</div>
              <RowHint style={{ marginTop: 1 }}>Invited {when(inv.invitedAt || inv.lastSentAt)}</RowHint>
            </>
          ) : null}
        </td>
        <td>{isOwnerSeat ? <RolePill $tone="admin">Admin</RolePill> : holder ? <RolePill $tone="member">Member</RolePill> : <RolePill $tone="pending">{inv ? "Invited" : "Open"}</RolePill>}</td>
        <td className="muted">{x.planName}</td>
        <td className="muted">{holder ? when(x.since) : inv ? "Pending" : "—"}</td>
        {owner && (
          <td className="amount">
            {holder && !isOwnerSeat && (
              <RowMenu label={`Actions for ${holder.name || holder.email}`}>
                <button type="button" role="menuitem" onClick={() => (window.location.href = "/checkout")}>
                  Change plan
                  <small>every seat, and how you're billed</small>
                </button>
                <div className="sep" />
                <button type="button" role="menuitem" onClick={() => setPromoting(holder)}>
                  Make admin
                </button>
                <button type="button" role="menuitem" className="danger" onClick={() => setRemoving(holder)}>
                  Remove
                </button>
              </RowMenu>
            )}
            {inv && (
              <TableActions>
                <button type="button" disabled={busyId === inv.id} onClick={() => inviteAction(inv, "resend")}>
                  Resend
                </button>
                <button type="button" className="danger" disabled={busyId === inv.id} onClick={() => inviteAction(inv, "cancel")}>
                  Cancel
                </button>
              </TableActions>
            )}
          </td>
        )}
      </FadeIn>
    );
  };

  return (
    <>
      {removing && (
        <RemoveMemberModal
          member={removing}
          onClose={() => setRemoving(null)}
          onRemoved={(body) => {
            setTeam(body);
            setRemoving(null);
          }}
        />
      )}
      {promoting && (
        <MakeAdminModal
          member={promoting}
          onClose={() => setPromoting(null)}
          onDone={(body) => {
            setTeam(body);
            setPromoting(null);
          }}
        />
      )}
      <SettingsCard title="Workspace" sub="Everyone here shares the company's searches, shortlists and tracked companies, each with their own credits.">
        <SettingRow label="Company">{loading ? <Skeleton width={140} height={14} inline /> : <span>{team?.companyName || "—"}</span>}</SettingRow>
        {!loading && !owner && (
          <SettingRow label="Account admin" hint="Only the admin can invite or remove people, change seats, or buy credits.">
            <span>{team?.ownerEmail || "—"}</span>
          </SettingRow>
        )}
        {!loading && owner && !paid && (
          <SettingRow label="Seats" hint="Inviting colleagues needs a paid plan. Each seat is one person with their own plan and credits.">
            <span>Just you</span>
          </SettingRow>
        )}
      </SettingsCard>
      {paid && (
        <>
          <div style={{ height: 18 }} />
          <SettingsCard
            title="Seats"
            sub={
              owner
                ? "One seat per person, each with its own plan and its own credits every month. Invite someone into a free seat, or change a seat's plan from its dropdown. Your own plan changes on the plans page."
                : "The seats on this workspace's plan."
            }
            actions={
              owner ? (
                <GhostButton type="button" onClick={() => (window.location.href = "/plans?view=seats")}>
                  Add seats
                </GhostButton>
              ) : null
            }
          >
            {error && <RowHint style={{ marginBottom: 12 }}>{error}</RowHint>}
            {!loading && (
              <RowHint style={{ marginTop: 0, marginBottom: 12 }}>
                {filled} of {seatList.length} seat{seatList.length === 1 ? "" : "s"} filled · {tierLabel}
                {team?.cycle ? `, billed ${team.cycle}` : ""}
              </RowHint>
            )}
            <UsageTable className="seats">
              <thead>
                <tr>
                  <th>Seat</th>
                  <th>Who</th>
                  <th>Role</th>
                  <th>Plan</th>
                  <th>Since</th>
                  {owner && <th className="amount" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 2 }).map((_, i) => (
                      <tr key={`sk-${i}`} aria-hidden="true">
                        <td><Skeleton width={44} height={13} /></td>
                        <td><Skeleton width={`${55 + ((i * 13) % 25)}%`} height={13} /></td>
                        <td><Skeleton width={54} height={13} /></td>
                        <td><Skeleton width={60} height={13} /></td>
                        <td><Skeleton width={84} height={13} /></td>
                        {owner && <td className="amount" />}
                      </tr>
                    ))
                  : seatList.map(seatRow)}
              </tbody>
            </UsageTable>
          </SettingsCard>
        </>
      )}
      {!loading && unseated.length > 0 && (
        <>
          <div style={{ height: 18 }} />
          <SettingsCard
            title="Also in this workspace"
            sub={
              owner
                ? "People who joined before seats existed. They keep their own plan and credits until you give them a seat."
                : "People in the workspace without a seat on the plan."
            }
          >
            <UsageTable className="people">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Plan</th>
                  <th>Since</th>
                  {owner && <th className="amount" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {unseated.map((m) => (
                  <FadeIn as="tr" key={m.userId}>
                    <td>{personCell(m, false)}</td>
                    <td className="muted">Their own</td>
                    <td className="muted">{when(m.joinedAt)}</td>
                    {owner && (
                      <td className="amount">
                        <TableActions>
                          {openSeats.length > 1 && (
                            <select
                              value={seatFor[m.userId] || openSeats[0].id}
                              onChange={(e) => setSeatFor((v) => ({ ...v, [m.userId]: e.target.value }))}
                              aria-label="Seat to assign"
                              style={{ height: 28, borderRadius: 8, padding: "0 6px", font: "inherit", fontSize: 12.5 }}
                            >
                              {openSeats.map((x) => (
                                <option key={x.id} value={x.id}>
                                  Seat {seatList.findIndex((y) => y.id === x.id) + 1} · {x.planName}
                                </option>
                              ))}
                            </select>
                          )}
                          <button type="button" disabled={!openSeats.length || busyId === m.userId} onClick={() => assign(m)} title={openSeats.length ? "" : "No free seat"}>
                            {openSeats.length === 1 ? `Give seat ${seatList.findIndex((y) => y.id === openSeats[0].id) + 1}` : "Give seat"}
                          </button>
                          <button type="button" className="danger" onClick={() => setRemoving(m)}>
                            Remove
                          </button>
                        </TableActions>
                      </td>
                    )}
                  </FadeIn>
                ))}
              </tbody>
            </UsageTable>
          </SettingsCard>
        </>
      )}
    </>
  );
}

// ─── Referrals panel ──────────────────────────────────────────
const RefLinkBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 12px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  background: ${({ theme }) => theme.bg};
  min-width: 0;

  code {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    color: ${({ theme }) => theme.ink};
  }
`;

const RefCodeLine = styled.div`
  margin-top: 10px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  b {
    font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.ink};
  }
  button {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    color: ${({ theme }) => theme.accent};
    cursor: pointer;
  }
`;

const RewardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 18px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const RewardTile = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 12px;
  padding: 14px 16px;

  .who {
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .amt {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 30px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.ink};
    margin-top: 4px;
  }
  .amt span {
    font-size: 14px;
    color: ${({ theme }) => theme.inkMuted};
    margin-left: 6px;
    letter-spacing: 0;
  }
  .terms {
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
    margin-top: 6px;
    line-height: 1.45;
  }
`;

const RefSteps = styled.ol`
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: grid;
  gap: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.inkMuted};
  line-height: 1.5;

  li {
    display: flex;
    gap: 10px;
  }
  li > span {
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 99px;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 600;
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.accent};
  }
`;

const RefSummary = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 6px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.inkMuted};

  b {
    display: block;
    font-family: "Instrument Serif", Georgia, serif;
    font-weight: 400;
    font-size: 26px;
    line-height: 1.1;
    color: ${({ theme }) => theme.ink};
  }
`;

const FriendRow = styled.div`
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.line};

  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }
  .name {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.ink};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .when {
    flex: none;
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .note {
    margin-top: 10px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .note.good {
    color: ${({ theme }) => theme.ink};
  }
`;

// Three stages across the full width (first left, middle centred, last right), on one track that fills
// as the friend moves along; --fill is how far it's coloured (0–100%).
const Tracker = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 12px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 7px;
    left: 8px;
    height: 2px;
    border-radius: 2px;
  }
  &::before {
    right: 8px;
    background: ${({ theme }) => theme.line};
  }
  &::after {
    width: calc((100% - 16px) * var(--fill, 0));
    background: ${({ theme }) => theme.accent};
  }

  .stage {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
  }
  .stage:nth-child(2) {
    align-items: center;
    text-align: center;
  }
  .stage:nth-child(3) {
    align-items: flex-end;
    text-align: right;
  }
  .dot {
    position: relative;
    z-index: 1;
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border-radius: 99px;
    border: 2px solid ${({ theme }) => theme.line};
    background: ${({ theme }) => theme.surface};
    display: grid;
    place-items: center;
    color: var(--on-fill);
  }
  .stage.done .dot {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
  }
  .stage.now .dot {
    border-color: ${({ theme }) => theme.accent};
  }
  .stage.done,
  .stage.now {
    color: ${({ theme }) => theme.ink};
  }
  .stage.missed .dot {
    border-style: dashed;
  }
  .stage.missed {
    text-decoration: line-through;
  }
`;

const refDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const daysLeft = (v) => Math.max(0, Math.ceil((new Date(v).getTime() - Date.now()) / 86400000));

function FriendProgress({ f }) {
  const paid = f.status === "rewarded";
  const missed = f.status === "expired";
  const credited = paid && f.credits > 0;
  const stage = (cls, label) => (
    <div className={`stage ${cls}`}>
      <span className="dot">{cls === "done" && <FiCheck size={9} strokeWidth={4} aria-hidden="true" />}</span>
      {label}
    </div>
  );
  let note;
  let good = false;
  if (credited) {
    note = `+${formatCredits(f.credits)} credits added ${refDate(f.paidAt)}, use by ${refDate(f.expiresAt)}`;
    good = true;
  } else if (paid) note = "They joined, but you weren't on a paid plan at the time, so no credits were added.";
  else if (missed) note = "They didn't go on a paid plan before their free trial ended.";
  else {
    const d = daysLeft(f.deadline);
    note = `Waiting for a paid plan: ${d} day${d === 1 ? "" : "s"} left in their free trial (ends ${refDate(f.deadline)}).`;
  }
  return (
    <FriendRow>
      <div className="head">
        <span className="name">{f.name}</span>
        <span className="when">Signed up {refDate(f.signedUpAt)}</span>
      </div>
      <Tracker aria-label={note} style={{ "--fill": credited ? 1 : missed ? 0 : 0.5 }}>
        {stage("done", "Signed up")}
        {stage(paid ? "done" : missed ? "missed" : "now", "Paid plan")}
        {stage(credited ? "done" : paid ? "missed" : "", "Credits added")}
      </Tracker>
      <div className={`note${good ? " good" : ""}`}>{note}</div>
    </FriendRow>
  );
}

// The friend's side while they're not on a paid plan. Codes attach only at sign-up, so nothing here adds one.
const refDateYear = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const RefereeHero = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px;
  border-radius: 14px;
  background: ${({ theme, $failed }) => ($failed ? theme.bg : theme.accentSoft)};
  border: 1px solid ${({ theme, $failed }) => ($failed ? theme.line : "transparent")};

  .icon {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: ${({ theme, $failed }) => ($failed ? theme.line : theme.accent)};
    color: ${({ theme, $failed }) => ($failed ? theme.inkMuted : "var(--on-fill)")};
  }
  .title {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    line-height: 1.15;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  .lede {
    margin-top: 6px;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${({ theme }) => theme.inkMuted};
  }
  .lede b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
  @media (max-width: 560px) {
    flex-direction: column;
    padding: 16px;
  }
`;

// On a paid referee's referrer page: how they joined and the bonus they got.
const RefJoinedBanner = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;

  .icon {
    flex: none;
    width: 22px;
    height: 22px;
    margin-top: 1px;
    border-radius: 99px;
    display: grid;
    place-items: center;
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.accent};
  }
  .text {
    font-size: 13.5px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkMuted};
  }
  .text b {
    color: ${({ theme }) => theme.ink};
    font-weight: 600;
  }
`;

function RefereeView({ me, rewards }) {
  const amount = formatCredits(me.credits);
  const months = rewards?.refereeMonths || 1;
  const monthsLabel = `${months} month${months === 1 ? "" : "s"}`;
  const deadline = me.deadline;
  const left = deadline ? daysLeft(deadline) : 0;
  const stage = (cls, label) => (
    <div className={`stage ${cls}`}>
      <span className="dot">{cls === "done" && <FiCheck size={9} strokeWidth={4} aria-hidden="true" />}</span>
      {label}
    </div>
  );
  const tiles = (
    <RewardGrid>
      <RewardTile>
        <div className="who">Your bonus</div>
        <div className="amt">
          {amount}
          <span>credits</span>
        </div>
        <div className="terms">
          On top of your plan's credits. Use them within {monthsLabel}. <UsdNote style={{ marginLeft: 0 }}>{formatCreditsUsd(me.credits)}</UsdNote>
        </div>
      </RewardTile>
      <RewardTile>
        <div className="who">Time left to claim it</div>
        <div className="amt">
          {left}
          <span>day{left === 1 ? "" : "s"}</span>
        </div>
        <div className="terms">Your free trial ends on {refDateYear(deadline)}. Go on a paid plan before then.</div>
      </RewardTile>
    </RewardGrid>
  );

  if (me.status === "signed_up")
    return (
      <SettingsCard title="Referral code" sub="You signed up with a friend's code.">
        <RefereeHero>
          <span className="icon">
            <FiCheck size={20} aria-hidden="true" />
          </span>
          <div>
            <div className="title">You're all set</div>
            <div className="lede">
              Go on a paid plan before <b>{refDateYear(deadline)}</b> and <b>{amount} bonus credits</b> are added to your account.
            </div>
          </div>
        </RefereeHero>
        <Tracker style={{ marginTop: 20, "--fill": 0.5 }}>
          {stage("done", "Signed up")}
          {stage("now", "Paid plan")}
          {stage("", "Bonus credits")}
        </Tracker>
        {tiles}
        <div style={{ marginTop: 18 }}>
          <PrimaryBtn type="button" onClick={() => (window.location.href = "/plans")}>
            See plans
          </PrimaryBtn>
        </div>
      </SettingsCard>
    );

  if (me.status === "rewarded")
    return (
      <SettingsCard title="Referral bonus" sub="From the friend who referred you.">
        <RefereeHero>
          <span className="icon">
            <FiGift size={20} aria-hidden="true" />
          </span>
          <div>
            <div className="title">{amount} credits added</div>
            <div className="lede">Thanks for joining. Bonus credits are used after your plan credits, before any extras. They last {monthsLabel} from when you went on a paid plan.</div>
          </div>
        </RefereeHero>
        <Tracker style={{ marginTop: 20, "--fill": 1 }}>
          {stage("done", "Signed up")}
          {stage("done", "Paid plan")}
          {stage("done", "Bonus credits")}
        </Tracker>
        <div style={{ marginTop: 18 }}>
          <Btn type="button" onClick={() => (window.location.href = "/settings?tab=credits")}>
            View credits
          </Btn>
        </div>
      </SettingsCard>
    );

  if (me.status === "expired")
    return (
      <SettingsCard title="Referral code" sub="You signed up with a friend's code.">
        <RefereeHero $failed>
          <span className="icon">
            <FiX size={20} aria-hidden="true" />
          </span>
          <div>
            <div className="title">Your referral bonus has lapsed</div>
            <div className="lede">
              You didn't join a paid plan before your free trial ended{deadline ? <> on <b>{refDateYear(deadline)}</b></> : null}.
            </div>
          </div>
        </RefereeHero>
        <Tracker style={{ marginTop: 20, "--fill": 0 }}>
          {stage("done", "Signed up")}
          {stage("missed", "Paid plan")}
          {stage("missed", "Bonus credits")}
        </Tracker>
        <RewardGrid>
          <RewardTile style={{ opacity: 0.6 }}>
            <div className="who">Your bonus</div>
            <div className="amt">
              <s>{amount}</s>
              <span>credits</span>
            </div>
            <div className="terms">Not claimed. It needed a paid plan before your free trial ended.</div>
          </RewardTile>
          <RewardTile style={{ opacity: 0.6 }}>
            <div className="who">Time left to claim it</div>
            <div className="amt">
              0<span>days</span>
            </div>
            <div className="terms">Your free trial ended on {refDateYear(deadline)}.</div>
          </RewardTile>
        </RewardGrid>
        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <PrimaryBtn type="button" onClick={() => (window.location.href = "/plans")}>
            See plans
          </PrimaryBtn>
          <RowHint style={{ margin: 0 }}>You can still choose a plan any time, without the bonus.</RowHint>
        </div>
      </SettingsCard>
    );
  return null;
}

function ReferralsPanel() {
  const { showToast } = useToast();
  const [state, setState] = React.useState(null);
  const [error, setError] = React.useState("");
  const load = React.useCallback(() => {
    return fetchWithAuth("/api/referrals")
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok || !body) throw new Error(body?.error || "We couldn't load your referrals just now.");
        setState(body);
      })
      .catch((err) => setError(err.message));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);
  const link = state?.code ? `${window.location.origin}/signup?ref=${state.code}` : "";
  // The link goes out inside a ready-to-send message, so a paste into chat or email reads as an invitation.
  const invite = state?.rewards
    ? `I'm using scoutm8 to find candidates and thought you'd like it too. Sign up with my link and you'll get ${formatCredits(state.rewards.referee)} bonus credits when you go on a paid plan: ${link}`
    : link;
  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${what} copied`);
    } catch {
      showToast("Couldn't copy. Select it and copy by hand.");
    }
  };
  const r = state?.rewards;
  const friends = state?.friends || [];
  const joined = friends.filter((f) => f.status === "rewarded").length;
  const earned = friends.reduce((n, f) => n + (f.credits || 0), 0);

  if (error)
    return (
      <SettingsCard title="Refer a friend" sub="Share scoutm8 and you both get bonus credits.">
        <RowHint>{error}</RowHint>
      </SettingsCard>
    );
  if (!state)
    return (
      <SettingsCard title="Refer a friend" sub="Share scoutm8 and you both get bonus credits.">
        <Skeleton height={44} />
        <div style={{ height: 18 }} />
        <Skeleton height={96} />
      </SettingsCard>
    );
  // Not on a paid plan but signed up with a referral: their own progress (or how it fell through).
  if (!state.eligible && state.referredBy) return <RefereeView me={state.referredBy} rewards={state.rewards} />;
  // The tab only shows for paid users and referred sign-ups, so anything else has nothing to show here.
  if (!state.eligible) return null;

  const joinedVia = state.referredBy?.status === "rewarded" ? state.referredBy : null;
  return (
    <>
      <SettingsCard title="Refer a friend" sub="Share your link. When a friend goes on a paid plan, you both get bonus credits.">
        <RefLinkBox>
          <code title={link}>{link}</code>
          <PrimaryBtn type="button" $sm onClick={() => copy(invite, "Invite")} title="Copies a short message with your link in it">
            Copy invite
          </PrimaryBtn>
        </RefLinkBox>
        <RefCodeLine>
          Or share your code <b>{state.code}</b>
          <button type="button" onClick={() => copy(state.code, "Code")}>
            Copy
          </button>
          <span>— they enter it on the sign-up form.</span>
        </RefCodeLine>
        <RewardGrid>
          <RewardTile>
            <div className="who">You get</div>
            <div className="amt">
              {formatCredits(r.referrer)}
              <span>credits</span>
            </div>
            <div className="terms">
              For every friend who joins. Use them within {r.referrerMonths} months. <UsdNote style={{ marginLeft: 0 }}>{formatCreditsUsd(r.referrer)}</UsdNote>
            </div>
          </RewardTile>
          <RewardTile>
            <div className="who">Your friend gets</div>
            <div className="amt">
              {formatCredits(r.referee)}
              <span>credits</span>
            </div>
            <div className="terms">
              On top of their plan. Use them within {r.refereeMonths} month{r.refereeMonths === 1 ? "" : "s"}. <UsdNote style={{ marginLeft: 0 }}>{formatCreditsUsd(r.referee)}</UsdNote>
            </div>
          </RewardTile>
        </RewardGrid>
        <RefSteps>
          <li><span>1</span>Your friend signs up with your link, or enters your code on the sign-up form. Codes can't be added after sign-up.</li>
          <li><span>2</span>They go on a paid plan before their {r.windowDays}-day free trial ends.</li>
          <li><span>3</span>Credits land in both accounts, as long as you're still on a paid plan then. Bonus credits are used after plan credits, before any extras.</li>
        </RefSteps>
      </SettingsCard>
      <div style={{ height: 18 }} />
      <SettingsCard title="Your referrals" sub="Everyone who signed up with your link, and where they are.">
        {friends.length === 0 ? (
          <RowHint>No one yet. Share your link and you'll see each friend's progress here.</RowHint>
        ) : (
          <>
            <RefSummary>
              <div>
                <b>{friends.length}</b>signed up
              </div>
              <div>
                <b>{joined}</b>on a paid plan
              </div>
              <div>
                <b>{formatCredits(earned)}</b>credits earned
              </div>
            </RefSummary>
            {friends.map((f) => (
              <FadeIn key={f.id}>
                <FriendProgress f={f} />
              </FadeIn>
            ))}
          </>
        )}
      </SettingsCard>
      {joinedVia && (
        <>
          <div style={{ height: 18 }} />
          <SettingsCard title="How you joined" sub="Your own referral bonus.">
            <RefJoinedBanner>
              <span className="icon">
                <FiCheck size={14} strokeWidth={3} aria-hidden="true" />
              </span>
              <div className="text">
                You joined through <b>{joinedVia.referrerName ? `${joinedVia.referrerName}'s` : "a friend's"} referral</b>, and{" "}
                <b>{formatCredits(joinedVia.credits)} bonus credits</b> were added to your account
                {joinedVia.expiresAt ? `, use them by ${refDateYear(joinedVia.expiresAt)}` : ""}.
              </div>
            </RefJoinedBanner>
          </SettingsCard>
        </>
      )}
    </>
  );
}

function CreditsPanel() {
  const { balance, loading: balanceLoading } = useCredits();
  const { showToast } = useToast();
  // Referral credits (with their use-by dates) and, for a referred sign-up, the bonus still to unlock.
  const [referral, setReferral] = React.useState(null);
  const loadReferral = React.useCallback(() => {
    fetchWithAuth("/api/referrals")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => body && setReferral(body))
      .catch(() => {});
  }, []);
  React.useEffect(() => {
    loadReferral();
  }, [loadReferral, balance]);
  const [plan, setPlan] = React.useState(null);
  const [planLoading, setPlanLoading] = React.useState(true);
  const [cancelling, setCancelling] = React.useState(false);
  const loadPlan = React.useCallback(() => {
    return fetchWithAuth("/api/billing/status")
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (res.ok && body) setPlan(body);
      })
      .catch(() => {})
      .finally(() => setPlanLoading(false));
  }, []);
  React.useEffect(() => {
    loadPlan();
  }, [loadPlan]);
  const planDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const cancelPlan = () => setCancelOpen(true);
  // Undo a cancellation or a change waiting for the renewal date.
  const keepPlan = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const res = await fetchWithAuth("/api/billing/resume", { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't update the plan just now.");
      setPlan(body);
      showToast("Your plan carries on as it is");
    } catch (err) {
      showToast(err.message || "We couldn't update the plan just now.");
    } finally {
      setCancelling(false);
    }
  };
  const scheduled = Boolean(plan?.cancelAtPeriodEnd || plan?.pendingChange);
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [rowsLoading, setRowsLoading] = React.useState(true);
  // Search runs fold their setup steps (brief, filter tuning) underneath; open ones by id.
  const [openRows, setOpenRows] = React.useState(() => new Set());
  const toggleRow = (id) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // First-load placeholders fill the screen instead of a fixed handful of rows.
  const usageSkelRef = React.useRef(null);
  const usageSkelCount = useFillCount(usageSkelRef, {
    min: 6,
    enabled: rowsLoading && rows.length === 0,
  });
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    setRowsLoading(true);
    fetchWithAuth(`/api/credits/usage?page=${page}&limit=25`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || "Could not load usage.");
        if (!alive) return;
        setRows((prev) => (page === 1 ? body.rows || [] : [...prev, ...(body.rows || [])]));
        setHasMore(!!body.hasMore);
        setError("");
      })
      .catch((err) => alive && setError(err.message || "Could not load usage."))
      .finally(() => alive && setRowsLoading(false));
    return () => {
      alive = false;
    };
  }, [page]);

  const when = (at) => {
    const d = new Date(at);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
  };

  return (
    <>
      {cancelOpen && (
        <CancelPlanModal
          plan={plan}
          onClose={() => setCancelOpen(false)}
          onCancelled={(body) => {
            setPlan(body);
            setCancelOpen(false);
          }}
        />
      )}
      <SettingsCard
        title="Plan"
        sub={
          plan?.role === "member"
            ? `Your workspace's plan, managed by ${plan.managedBy || "your account admin"}. It tops up your balance every month.`
            : "Your subscription tops up the balance below every month."
        }
        actions={
          plan?.role === "member" ? null : (
          <div style={{ display: "flex", gap: 8 }}>
            <PrimaryBtn type="button" onClick={() => (window.location.href = "/plans")}>
              Change plan
            </PrimaryBtn>
            {plan?.plan && ["ACTIVE", "UNPAID"].includes(plan.status) && scheduled && (
              <GhostButton type="button" onClick={keepPlan} disabled={cancelling}>
                {cancelling ? "Saving…" : "Keep current plan"}
              </GhostButton>
            )}
            {plan?.plan && ["ACTIVE", "UNPAID"].includes(plan.status) && !scheduled && (
              <DangerOutlineButton type="button" onClick={cancelPlan} disabled={cancelling}>
                Cancel plan
              </DangerOutlineButton>
            )}
          </div>
          )
        }
      >
        <SettingRow label="Your current plan">
          {planLoading ? (
            <Skeleton width={120} height={14} inline />
          ) : plan?.planName ? (
            <span>
              {plan.planName}
              {plan.cycle ? `, billed ${plan.cycle}` : ""}
              {plan.status === "TRIAL" && " (free trial)"}
              {plan.status === "TRIAL_ENDED" && " (trial ended)"}
              {plan.status === "CANCELLED" && " (cancelled)"}
              {plan.status === "UNPAID" && " (payment failed)"}
            </span>
          ) : plan?.status === "TRIAL_USED" ? (
            <span>No plan yet. This email has already had its free trial.</span>
          ) : (
            <RowHint>No plan yet.</RowHint>
          )}
        </SettingRow>
        {plan?.status === "TRIAL" && plan.trialEndsAt && (
          <SettingRow label="Trial ends">
            <span>{new Date(plan.trialEndsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          </SettingRow>
        )}
        {plan?.creditsPerMonth && (
          <SettingRow label="Monthly credits" tour="credits-monthly">
            <span>
              {formatCredits(plan.creditsPerMonth)}
              <UsdNote>{formatCreditsUsd(plan.creditsPerMonth)}</UsdNote>
            </span>
          </SettingRow>
        )}
        {plan?.trialCredits && plan.status === "TRIAL" && (
          <SettingRow label="Trial credits">
            <span>
              {formatCredits(plan.trialCredits)}
              <UsdNote>{formatCreditsUsd(plan.trialCredits)}</UsdNote>
            </span>
          </SettingRow>
        )}
        {plan?.changeError && (
          <SettingRow label="Needs attention">
            <span>{plan.changeError}</span>
          </SettingRow>
        )}
        {plan?.pendingChange && (
          <SettingRow label={`Changes on ${planDate(plan.pendingChange.effectiveAt)}`} hint="Your current plan runs until then.">
            <span>
              {plan.pendingChange.planName}, billed {plan.pendingChange.cycle}
              {Number(plan.pendingChange.seats) > 1 ? `, ${plan.pendingChange.seats} seats` : ""}
            </span>
          </SettingRow>
        )}
        {plan?.cancelAtPeriodEnd && plan.endsAt && (
          <SettingRow label="Plan ends" hint="Nothing more is charged. Credits stay usable until then.">
            <span>{planDate(plan.endsAt)}</span>
          </SettingRow>
        )}
        {plan?.status === "ACTIVE" && !plan.cancelAtPeriodEnd && plan.renewsAt && (
          <SettingRow label="Renews">
            <span>{planDate(plan.renewsAt)}</span>
          </SettingRow>
        )}
        {plan?.nextCharge && (
          <SettingRow label="Next charge" hint={plan.nextCharge.maintenanceMonthly > 0 ? `Plus ${money(plan.nextCharge.maintenanceMonthly)}/month for tracked companies.` : undefined}>
            <span>
              {money(plan.nextCharge.amount)}
              {plan.nextCharge.at ? ` on ${planDate(plan.nextCharge.at)}` : ""}
              {plan.nextCharge.cycle === "yearly" ? ", for the year" : ""}
            </span>
          </SettingRow>
        )}
      </SettingsCard>
      <div style={{ height: 18 }} />
      <SettingsCard
        title="Credits"
        tour="credits-card"
        sub="Searches, previews, filter tuning and contact lookups draw from this balance as they run."
        actions={
          plan?.role === "member" ? null : (
            <PrimaryBtn type="button" onClick={() => (window.location.href = "/checkout?topup=1")} data-tour="credits-buy">
              Buy extra credits
            </PrimaryBtn>
          )
        }
      >
        <SettingRow label="Available balance" tour="credits-balance">
          <BalanceFigure>
            {balanceLoading && balance == null ? "…" : formatCredits(balance)}
            <span>credits</span>
          </BalanceFigure>
          {balance != null && <UsdNote $block data-tour="credits-usd">{formatCreditsUsd(balance)} USD</UsdNote>}
        </SettingRow>
        {referral?.bonuses?.length > 0 && (
          <SettingRow label="Referral credits" hint="Used after your plan credits. Unused ones expire on the date shown.">
            {referral.bonuses.map((b) => (
              <div key={String(b.expiresAt)}>
                {formatCredits(b.left)} left
                <UsdNote>use by {planDate(b.expiresAt)}</UsdNote>
              </div>
            ))}
          </SettingRow>
        )}
        {referral?.referredBy?.status === "signed_up" && (
          <SettingRow
            label="Referral bonus"
            hint={`Go on a paid plan before your free trial ends on ${planDate(referral.referredBy.deadline)} and ${formatCredits(referral.referredBy.credits)} bonus credits are added.`}
          >
            <Btn type="button" $sm onClick={() => (window.location.href = "/plans")}>
              See plans
            </Btn>
          </SettingRow>
        )}
      </SettingsCard>
      <div style={{ height: 18 }} />
      <SettingsCard title="Usage" sub="Every run and every top-up, newest first." tour="credits-usage">
        {error && <RowHint style={{ marginBottom: 12 }}>{error}</RowHint>}
        {!rowsLoading && !error && rows.length === 0 ? (
          <RowHint>Nothing yet — your first search will show up here.</RowHint>
        ) : (
          <UsageTable>
            <thead>
              <tr>
                <th>When</th>
                <th>Activity</th>
                <th>Reference</th>
                <th className="amount">Credits</th>
              </tr>
            </thead>
            <tbody ref={usageSkelRef}>
              {rowsLoading && rows.length === 0
                ? // First load: rows the same height as real ones, widths varied so it reads as a list.
                  Array.from({ length: usageSkelCount }).map((_, i) => (
                    <tr key={`sk-${i}`} aria-hidden="true">
                      <td><Skeleton width={i % 2 ? 96 : 104} height={13} /></td>
                      <td><Skeleton width={i % 3 ? 118 : 96} height={13} /></td>
                      <td><Skeleton width={`${45 + ((i * 17) % 40)}%`} height={13} /></td>
                      <td className="amount"><Skeleton width={44} height={13} /></td>
                    </tr>
                  ))
                : rows.map((r) => {
                    if (r.type === "renewal") {
                      const rn = r.renewal || {};
                      const open = openRows.has(r.id);
                      const parts = rn.carried?.parts || [];
                      const month = (v) => new Date(v).toLocaleDateString(undefined, { month: "long" });
                      const day = (v) => new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
                      const summary = rn.plan ? `${rn.plan} plan` : "Plan";
                      return (
                        <React.Fragment key={r.id}>
                          <FadeIn as="tr" className="expandable" onClick={() => toggleRow(r.id)} aria-expanded={open}>
                            <td className="muted">{when(r.at)}</td>
                            <td>
                              <FiChevronRight size={14} className={`chev${open ? " open" : ""}`} aria-hidden="true" />
                              Plan renewed
                            </td>
                            <td className="muted" title={summary}>{summary}</td>
                            <td className="amount">
                              {r.amount > 0 ? "+" : ""}
                              {formatCredits(r.amount)}
                            </td>
                          </FadeIn>
                          {open && (
                            <>
                              <tr className="sub">
                                <td className="muted" />
                                <td>New plan credits</td>
                                <td className="muted">{[rn.plan, month(r.at)].filter(Boolean).join(", ")}</td>
                                <td className="amount">+{formatCredits(rn.granted)}</td>
                              </tr>
                              {rn.expired && (
                                <tr className="sub">
                                  <td className="muted" />
                                  <td>Expired</td>
                                  <td className="muted">{rn.expired.note}</td>
                                  <td className="amount">{formatCredits(rn.expired.amount)}</td>
                                </tr>
                              )}
                              {parts.map((p, i) => (
                                <tr className="sub" key={`${r.id}-c${i}`}>
                                  <td className="muted" />
                                  <td>Carried over from {month(p.from)}</td>
                                  <td className="muted">Expires {day(p.expiresAt)}</td>
                                  <td className="amount">{formatCredits(p.amount)}</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      );
                    }
                    if (r.type === "carryover") {
                      const parts = r.parts || [];
                      const open = openRows.has(r.id);
                      const month = (v) => new Date(v).toLocaleDateString(undefined, { month: "long", year: "numeric" });
                      const day = (v) => new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
                      return (
                        <React.Fragment key={r.id}>
                          <FadeIn as="tr" className="expandable carry" onClick={() => toggleRow(r.id)} aria-expanded={open}>
                            <td className="muted">{when(r.at)}</td>
                            <td>
                              <FiChevronRight size={14} className={`chev${open ? " open" : ""}`} aria-hidden="true" />
                              Carried over
                            </td>
                            <td className="muted">
                              {formatCredits(r.total)} plan credits from {parts.length} month{parts.length === 1 ? "" : "s"}
                            </td>
                            <td className="amount muted">–</td>
                          </FadeIn>
                          {open &&
                            parts.map((p, i) => (
                              <tr className="sub" key={`${r.id}-${i}`}>
                                <td className="muted" />
                                <td>From {month(p.from)}</td>
                                <td className="muted">Expires {day(p.expiresAt)}</td>
                                <td className="amount muted">{formatCredits(p.amount)}</td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    }
                    const kids = r.children || [];
                    const open = kids.length > 0 && openRows.has(r.id);
                    // Truncated to one line in the table, so the full text rides along as a tooltip.
                    const refText =
                      r.ref?.title || r.ref?.name || r.ref?.companyName || r.ref?.req_no || r.note || "";
                    return (
                      <React.Fragment key={r.id}>
                        <FadeIn
                          as="tr"
                          className={kids.length ? "expandable" : undefined}
                          onClick={kids.length ? () => toggleRow(r.id) : undefined}
                          aria-expanded={kids.length ? open : undefined}
                        >
                          <td className="muted">{when(r.at)}</td>
                          <td>
                            {kids.length > 0 && (
                              <FiChevronRight
                                size={14}
                                className={`chev${open ? " open" : ""}`}
                                aria-hidden="true"
                              />
                            )}
                            {kids.length ? describeLedgerGroup(r) : describeLedgerRow(r)}
                          </td>
                          <td className="muted" title={refText || undefined}>
                            {refText}
                          </td>
                          <td className="amount">
                            {r.amount > 0 ? "+" : ""}
                            {formatCredits(r.amount)}
                          </td>
                        </FadeIn>
                        {open && (
                          <>
                            <tr className="sub">
                              <td className="muted">{when(r.at)}</td>
                              <td>{describeLedgerRow(r)}</td>
                              <td className="muted">{describeGroupPart(r, r, true)}</td>
                              <td className="amount">{formatCredits(r.ownAmount ?? r.amount)}</td>
                            </tr>
                            {kids.map((k) => (
                              <tr className="sub" key={k.id}>
                                <td className="muted">{when(k.at)}</td>
                                <td>{describeLedgerRow(k)}</td>
                                <td className="muted">{describeGroupPart(r, k, false)}</td>
                                <td className="amount">{formatCredits(k.amount)}</td>
                              </tr>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
            </tbody>
          </UsageTable>
        )}
        {hasMore && (
          <UsageFoot>
            <GhostButton
              type="button"
              disabled={rowsLoading}
              onClick={() => setPage((p) => p + 1)}
            >
              {rowsLoading ? "Loading…" : "Show more"}
            </GhostButton>
          </UsageFoot>
        )}
      </SettingsCard>
    </>
  );
}

// ─── Profile panel ────────────────────────────────────────────
/* Who is signed in, above the editable fields: avatar, display name, login email. */
const AccountHead = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding-bottom: 22px;
  margin-bottom: 22px;
  border-bottom: 1px solid ${({ theme }) => theme.line};

  .avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    font-size: 24px;
    font-weight: 600;
    background: ${({ theme }) => theme.accentSoft};
    color: ${({ theme }) => theme.accent};
    overflow: hidden;
  }
  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .name {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
    overflow-wrap: anywhere;
  }
  .email {
    margin-top: 4px;
    font-size: 14px;
    color: ${({ theme }) => theme.inkMuted};
    overflow-wrap: anywhere;
  }
`;

function ProfilePanel() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!user?.sub) return;
    getUserSettings(user.sub)
      .then((doc) => {
        setFirstName(String(doc?.firstName || ""));
        setLastName(String(doc?.lastName || ""));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [user?.sub]);

  async function save() {
    if (!user?.sub) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `/api/user-settings/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        },
      );
      if (!res.ok) throw new Error("Save failed");
      invalidateUserSettingsCache(user.sub);
      showToast("Saved your profile");
    } catch (err) {
      showToast(err.message || "Couldn't save profile");
    } finally {
      setSaving(false);
    }
  }

  const shownName = [firstName, lastName].map((v) => v.trim()).filter(Boolean).join(" ") || user?.name || user?.email || "";
  const initial = (shownName || "?").slice(0, 1).toUpperCase();

  return (
    <div style={{ display: "grid", gap: 20 }}>
    <SettingsCard
      title="Profile"
      sub="Your name as it appears in outreach signatures and across the workspace."
    >
      <AccountHead>
        <div className="avatar">{user?.picture ? <img src={user.picture} alt="" /> : initial}</div>
        <div>
          {loaded ? <div className="name">{shownName}</div> : <Skeleton width={180} height={26} radius="8px" />}
          <div className="email">{user?.email || ""}</div>
        </div>
      </AccountHead>
      {!loaded ? (
        // Reserve the exact layout of the two field rows + save button so the
        // form doesn't jump when the real inputs (36px) fade in.
        <>
          {[0, 1].map((i) => (
            <Row key={i}>
              <div>
                <Skeleton width={88} height={14} mb={8} />
                <Skeleton width={170} height={12} />
              </div>
              <Skeleton width="100%" height={36} radius="8px" style={{ maxWidth: 400 }} />
            </Row>
          ))}
          <div
            style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}
          >
            <Skeleton width={118} height={34} radius="8px" />
          </div>
        </>
      ) : (
        <FadeIn>
          <SettingRow
            label="First name"
            hint="Shown in outreach email signatures."
          >
            <TextInput
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              maxLength={100}
              style={{ maxWidth: 400 }}
            />
          </SettingRow>
          <SettingRow label="Last name" hint="Shown in outreach email signatures.">
            <TextInput
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              maxLength={100}
              style={{ maxWidth: 400 }}
            />
          </SettingRow>
          <div
            style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}
          >
            <PrimaryBtn onClick={save} disabled={saving}>
              {saving ? (
                <>
                  <Spinner /> Saving…
                </>
              ) : (
                "Save changes"
              )}
            </PrimaryBtn>
          </div>
        </FadeIn>
      )}
    </SettingsCard>
    <SettingsCard title="Delete account" sub="Close your account and remove its data from scoutm8.">
      <SettingRow label="Close your account" hint="This cannot be undone.">
        <DeleteAccount />
      </SettingRow>
    </SettingsCard>
    </div>
  );
}

// ─── Appearance panel ─────────────────────────────────────────
function AppearancePanel() {
  const { mode, toggleTheme } = useTheme();

  function setMode(target) {
    if (target === mode) return;
    if (target === "light" || target === "dark") toggleTheme();
  }

  return (
    <SettingsCard
      title="Appearance"
      sub="Visual preferences for your workspace."
    >
      <SettingRow label="Theme" hint="Choose your preferred color scheme.">
        <SegWrap>
          <SegBtn $active={mode === "light"} onClick={() => setMode("light")}>
            <Swatch $kind="light" /> Light
          </SegBtn>
          <SegBtn $active={mode === "dark"} onClick={() => setMode("dark")}>
            <Swatch $kind="dark" /> Dark
          </SegBtn>
        </SegWrap>
      </SettingRow>
    </SettingsCard>
  );
}

// ─── Mailbox panel ────────────────────────────────────────────
function MailboxPanel() {
  const { showToast } = useToast();
  const [status, setStatus] = React.useState(null);
  const [disconnecting, setDisconnecting] = React.useState(false);

  React.useEffect(() => {
    getMailStatus()
      .then(setStatus)
      .catch(() => setStatus({ connected: false }));
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("mail_connected");
    const err = params.get("mail_error");
    if (connected) {
      showToast(`${connected === "google" ? "Gmail" : "Outlook"} connected`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (err) {
      const reasons = {
        connect_init_failed:
          "Couldn't start the mailbox connection. The callback URL may not be registered in Auth0, or too many attempts were made — wait a minute and retry.",
        connect_complete_failed:
          "The provider approved, but Auth0 couldn't finish linking the mailbox. Please try again.",
        state_mismatch: "The connection attempt expired. Please try again.",
        no_refresh_token: "Please log out and back in, then connect the mailbox again.",
      };
      showToast(reasons[err] || "Couldn't connect your mailbox — please try again.", {
        duration: 8000,
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [showToast]);

  // Expired provider token: drop the dead link (best effort) and re-consent.
  async function handleReconnect(provider) {
    try {
      await unlinkMailbox();
    } catch (_) {
      // The old account may already be gone on Auth0's side; connecting still works.
    }
    startConnectMailbox(provider);
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await unlinkMailbox();
      setStatus(await getMailStatus());
      showToast("Mailbox disconnected");
    } catch (_) {
      showToast("Couldn't disconnect mailbox");
    } finally {
      setDisconnecting(false);
    }
  }

  function renderRow(provider, label, logo, color) {
    const isConnected =
      !!status?.connected && status.provider === provider;
    return (
      <MbRow key={provider}>
        <MbLeft>
          <MbLogo $color={color}>{logo}</MbLogo>
          <div>
            <MbName>{label}</MbName>
            <MbSub>
              {isConnected && status.needsReconnect ? (
                <b style={{ color: "var(--warn)", fontWeight: 600 }}>
                  {status.emailAddress ? `${status.emailAddress} — ` : ""}
                  connection expired, reconnect to keep sending
                </b>
              ) : isConnected ? (
                <>
                  <ConnectedDot />
                  <b style={{ color: "var(--success)", fontWeight: 600 }}>
                    {status.emailAddress || "Connected"}
                  </b>
                </>
              ) : (
                <span>
                  {provider === "google"
                    ? "Sync sent + replies via Google OAuth"
                    : "Sync sent + replies via Microsoft Graph"}
                </span>
              )}
            </MbSub>
          </div>
        </MbLeft>
        {isConnected && status.needsReconnect ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <DangerLink onClick={handleDisconnect} disabled={disconnecting}>
              Disconnect
            </DangerLink>
            <PrimaryBtn onClick={() => handleReconnect(provider)}>
              Reconnect
            </PrimaryBtn>
          </div>
        ) : isConnected ? (
          <DangerLink onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? (
              <>
                <Spinner /> Disconnecting…
              </>
            ) : (
              "Disconnect"
            )}
          </DangerLink>
        ) : (
          <PrimaryBtn
            onClick={() => startConnectMailbox(provider)}
            disabled={status === null}
          >
            Connect
          </PrimaryBtn>
        )}
      </MbRow>
    );
  }

  // While getMailStatus() is in flight, render skeleton rows that match the
  // MbRow layout (36px logo, name + sub line, right-aligned button) so the
  // card height is reserved and the real rows fade in without a flash.
  function renderSkeletonRow(key) {
    return (
      <MbRow key={key}>
        <MbLeft>
          <Skeleton width={36} height={36} radius="9px" />
          <div>
            <Skeleton width={70} height={14} mb={6} />
            <Skeleton width={180} height={12} />
          </div>
        </MbLeft>
        <Skeleton width={84} height={34} radius="8px" />
      </MbRow>
    );
  }

  return (
    <SettingsCard
      title="Mailbox Connection"
      sub="Connect a mailbox, then send outreach email from it across the platform."
    >
      {status === null ? (
        <>
          {renderSkeletonRow("sk-google")}
          {renderSkeletonRow("sk-microsoft")}
        </>
      ) : (
        <FadeIn>
          {renderRow("google", "Gmail", "G", PROVIDER_BRAND.google)}
          {renderRow("microsoft", "Outlook", "O", PROVIDER_BRAND.microsoft)}
        </FadeIn>
      )}
    </SettingsCard>
  );
}

// ─── Checklist panel (Company email list) ──────────────
// ─── Email alert controls ─────────────────────────────────────
const AlertBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px;
  margin-bottom: 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bg};
  border: 1px solid ${({ theme }) => theme.line};
  .left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .text b {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
  .text span {
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    color: ${({ theme }) => theme.inkMuted};
  }
`;

const Switch = styled.button`
  appearance: none;
  flex: none;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  position: relative;
  cursor: pointer;
  background: ${({ theme, $on }) => ($on ? theme.accent : theme.line)};
  transition: background 0.15s;
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? "21px" : "3px")};
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: left 0.15s;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  /* The phone-wide 44px button min-height turned the track into a tall blob with the knob at the top. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

function JobAlertControls() {
  const { showToast } = useToast();
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/job-alerts")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => alive && body && setState(body))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const toggle = async () => {
    if (busy || !state) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth("/api/job-alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !state.enabled }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't save that just now.");
      setState(body);
      showToast(body.enabled ? "Daily email on" : "Daily email off");
    } catch (err) {
      showToast(err.message);
    } finally {
      setBusy(false);
    }
  };
  const sendNow = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await fetchWithAuth("/api/job-alerts/send-now", { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "We couldn't send the email just now.");
      if (body.sent) {
        showToast(`Sent ${body.count} new role${body.count === 1 ? "" : "s"} to ${body.to}`);
        setState((s) => ({ ...(s || {}), lastSentAt: new Date().toISOString(), lastCount: body.count }));
      } else if (body.reason === "no_companies") showToast("Pick and save some companies first.");
      else if (body.reason === "nothing_new") showToast("No new roles at your companies in the last 7 days.");
      else showToast("Nothing was sent.");
    } catch (err) {
      showToast(err.message);
    } finally {
      setSending(false);
    }
  };
  const when = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
  const on = state?.enabled !== false;
  return (
    <AlertBar>
      <div className="left">
        <Switch type="button" role="switch" aria-checked={on} $on={on} onClick={toggle} disabled={busy || !state} aria-label="Daily email of new roles" data-tour="alerts-switch" />
        <div className="text">
          <b>{on ? "Daily email is on" : "Daily email is off"}</b>
          <span>
            {state?.lastSentAt
              ? `Last sent ${when(state.lastSentAt)}${state.lastCount != null ? `, ${state.lastCount} new role${state.lastCount === 1 ? "" : "s"}` : ""}.`
              : "Nothing sent yet. It goes out each morning when there is something new."}
          </span>
        </div>
      </div>
      <Btn type="button" onClick={sendNow} disabled={sending} data-tour="alerts-test">
        {sending && <Spinner />}
        {sending ? "Sending…" : "Send me a test"}
      </Btn>
    </AlertBar>
  );
}

function ChecklistPanel({ title, sub, field, extras = null }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [available, setAvailable] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set());
  const [initial, setInitial] = React.useState(new Set());
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    // Only the user's tracked companies (scout-tracked + industry picks) — same set as Research; imported-job companies are excluded.
    fetchWithAuth("/api/sources")
      .then((r) => r.json())
      .then((list) => {
        if (!mounted) return;
        const names = Array.isArray(list)
          ? list.map((x) => (typeof x === "string" ? x : x?.name || x?.source))
              .filter(Boolean)
          : [];
        setAvailable(names);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));

    if (user?.sub) {
      getUserSettings(user.sub)
        .then((doc) => {
          if (!mounted || !doc) return;
          const arr = Array.isArray(doc[field]) ? doc[field] : [];
          setSelected(new Set(arr));
          setInitial(new Set(arr));
        })
        .catch(() => {});
    }
    return () => {
      mounted = false;
    };
  }, [user?.sub, field]);

  const dirty = React.useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const s of selected) if (!initial.has(s)) return true;
    return false;
  }, [selected, initial]);

  function toggle(name) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }
  function selectAll() {
    setSelected(new Set(available));
  }
  function clearAll() {
    setSelected(new Set());
  }

  async function save() {
    if (!user?.sub) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `/api/user-settings/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [field]: Array.from(selected) }),
        },
      );
      if (!res.ok) throw new Error("Save failed");
      invalidateUserSettingsCache(user.sub);
      setInitial(new Set(selected));
      showToast("Saved your preferences");
    } catch (err) {
      showToast(err.message || "Couldn't save");
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    setSelected(new Set(initial));
  }

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase();
    const list = available.filter((f) => !q || f.toLowerCase().includes(q));
    // sort initial selections first so user doesn't lose their place
    return list.slice().sort((a, b) => {
      const ai = initial.has(a) ? 0 : 1;
      const bi = initial.has(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });
  }, [available, query, initial]);

  return (
    <SettingsCard title={title} sub={sub}>
      {extras}
      <ChToolbar>
        <SearchBox>
          <FiSearch size={14} />
          <input
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchBox>
        <ChCount>
          <b>{selected.size}</b> selected
        </ChCount>
        <Btn $sm onClick={selectAll}>
          Select all
        </Btn>
        <Btn $sm onClick={clearAll}>
          Clear
        </Btn>
      </ChToolbar>
      <ChGrid>
        {loading ? (
          // Skeleton rows matching the ChItem layout (16px check + name) laid
          // out in the same auto-fill grid so the list doesn't reflow on load.
          Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 10px",
              }}
            >
              <Skeleton width={16} height={16} radius="4px" />
              <Skeleton width={`${55 + ((i * 13) % 35)}%`} height={13} />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: 32,
              textAlign: "center",
              fontSize: 13,
              opacity: 0.6,
            }}
          >
            {query
              ? `No matches for "${query}"`
              : "No companies yet — track companies on the Jobs page or add them from Research."}
          </div>
        ) : (
          filtered.map((f) => {
            const on = selected.has(f);
            return (
              <ChItem key={f} $on={on}>
                <input type="checkbox" checked={on} onChange={() => toggle(f)} />
                <ChCheck $on={on}>
                  {on && <FiCheck size={12} strokeWidth={3} />}
                </ChCheck>
                <ChName>{f}</ChName>
              </ChItem>
            );
          })
        )}
      </ChGrid>
      {dirty && (
        <SaveBar>
          <span style={{ opacity: 0.75 }}>You have unsaved changes</span>
          <div style={{ flex: 1 }} />
          <Btn onClick={discard} disabled={saving}>
            Discard
          </Btn>
          <PrimaryBtn onClick={save} disabled={saving}>
            {saving ? (
              <>
                <Spinner /> Saving…
              </>
            ) : (
              "Save changes"
            )}
          </PrimaryBtn>
        </SaveBar>
      )}
    </SettingsCard>
  );
}

// ─── Prompts panel ────────────────────────────────────────────
// Prompts live in encrypted user_settings (same envelope as signatures), so
// they follow the user across devices and the Outreach composer can read them.
// Mirror of the server caps in /api/user-settings — keep in sync.
const PROMPT_BODY_MAX = 4000;
const PROMPTS_MAX_COUNT = 20;

function PromptsPanel() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [prompts, setPrompts] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState({ name: "", body: "" });
  const [saving, setSaving] = React.useState(false);

  // Save straight to the server. Optimistic state is set only after the POST
  // resolves so a rejected save (e.g. over the length cap) can't leave the
  // list showing something the server didn't keep.
  const persist = React.useCallback(
    async (next) => {
      if (!user?.sub) return;
      setSaving(true);
      try {
        const res = await fetchWithAuth(
          `/api/user-settings/${encodeURIComponent(user.sub)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ aiPrompts: next }),
          },
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Save failed");
        }
        invalidateUserSettingsCache(user.sub);
        setPrompts(next);
      } catch (err) {
        showToast(err.message || "Couldn't save prompt");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [user?.sub, showToast],
  );

  React.useEffect(() => {
    if (!user?.sub) return;
    let mounted = true;
    getUserSettings(user.sub)
      .then((doc) => {
        if (!mounted) return;
        setPrompts(Array.isArray(doc?.aiPrompts) ? doc.aiPrompts : []);
        setLoaded(true);
      })
      .catch(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, [user?.sub]);

  function startNew() {
    setEditing("new");
    setDraft({ name: "", body: "" });
  }
  function startEdit(p) {
    setEditing(p.id);
    setDraft({ name: p.name, body: p.body });
  }
  function cancel() {
    setEditing(null);
    setDraft({ name: "", body: "" });
  }
  async function save() {
    if (!draft.name.trim() || !draft.body.trim()) return;
    const clean = {
      name: draft.name.trim(),
      body: draft.body.trim().slice(0, PROMPT_BODY_MAX),
    };
    let next;
    if (editing === "new") {
      if (prompts.length >= PROMPTS_MAX_COUNT) {
        showToast(`You can save up to ${PROMPTS_MAX_COUNT} prompts`);
        return;
      }
      next = [
        {
          id: `p${Date.now()}`,
          ...clean,
          // First prompt becomes the default so the composer has something to
          // apply without a second trip to Settings.
          isDefault: prompts.length === 0,
        },
        ...prompts,
      ];
    } else {
      next = prompts.map((p) => (p.id === editing ? { ...p, ...clean } : p));
    }
    try {
      await persist(next);
      setEditing(null);
      setDraft({ name: "", body: "" });
      showToast("Prompt saved");
    } catch (_) {
      // toast already shown
    }
  }
  async function toggleDefault(id) {
    // Clicking the current default clears it — "no prompt applied" is a valid
    // state, so this is a toggle rather than signatures' one-way promotion.
    const wasDefault = prompts.find((p) => p.id === id)?.isDefault;
    try {
      await persist(
        prompts.map((p) => ({ ...p, isDefault: !wasDefault && p.id === id })),
      );
    } catch (_) {}
  }
  async function remove(id) {
    try {
      await persist(prompts.filter((p) => p.id !== id));
      if (editing === id) cancel();
    } catch (_) {}
  }

  // Close the editor modal on Escape (matches the signatures editor).
  React.useEffect(() => {
    if (editing === null) return;
    function onKey(e) {
      if (e.key === "Escape" && !saving) cancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, saving]);

  const editorModal =
    editing === null
      ? null
      : createPortal(
          <ModalOverlay onMouseDown={() => !saving && cancel()}>
            <PageScrollLock />
            <ModalCard $sm onMouseDown={(e) => e.stopPropagation()}>
              <EditTitle>
                {editing === "new" ? "Create a new prompt" : "Edit prompt"}
              </EditTitle>
              <Field data-tour="prompt-name">
                <label>Prompt name</label>
                <TextInput
                  placeholder="e.g. Friendly follow-up"
                  value={draft.name}
                  maxLength={80}
                  autoFocus
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <FieldHint>A short label so you can find this later.</FieldHint>
              </Field>
              <Field style={{ marginBottom: 0 }} data-tour="prompt-body">
                <label>Prompt text</label>
                <TextArea
                  rows={7}
                  placeholder="In this prompt, please…"
                  value={draft.body}
                  maxLength={PROMPT_BODY_MAX}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
                <FieldHint
                  style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
                >
                  <span style={{ flex: 1 }}>
                    Describe the email you want — purpose, angle, what to
                    mention, what to avoid. When selected it replaces the
                    standard pitch; your signature is added afterwards. You
                    can use placeholders like <code>{"{{role}}"}</code>,{" "}
                    <code>{"{{company}}"}</code>,{" "}
                    <code>{"{{firstName}}"}</code> — we'll fill them in per
                    recipient.
                  </span>
                  <span
                    style={{
                      flex: "0 0 auto",
                      fontVariantNumeric: "tabular-nums",
                      opacity:
                        draft.body.length > PROMPT_BODY_MAX * 0.9 ? 1 : 0.65,
                    }}
                  >
                    {draft.body.length}/{PROMPT_BODY_MAX}
                  </span>
                </FieldHint>
              </Field>
              <EditFoot>
                <Btn onClick={cancel} disabled={saving} data-tour="prompt-cancel">
                  Cancel
                </Btn>
                <PrimaryBtn
                  onClick={save}
                  disabled={saving || !draft.name.trim() || !draft.body.trim()}
                  data-tour="prompt-save"
                >
                  {saving ? (
                    <>
                      <Spinner /> Saving…
                    </>
                  ) : editing === "new" ? (
                    "Create prompt"
                  ) : (
                    "Save changes"
                  )}
                </PrimaryBtn>
              </EditFoot>
            </ModalCard>
          </ModalOverlay>,
          document.body,
        );

  return (
    <SettingsCard
      title="AI Prompts"
      sub="Saved prompts replace the default pitch when you pick one in the email composer."
    >
      {!loaded ? (
        // Skeleton rows matching PromptRow's grid (30px icon, name, actions)
        // so the list holds its space instead of popping in.
        <PromptList>
          {Array.from({ length: 3 }).map((_, i) => (
            <PromptRow key={i} as="div">
              <Skeleton width={30} height={30} radius="8px" />
              <div style={{ minWidth: 0 }}>
                <Skeleton width={130 + ((i * 40) % 90)} height={14} />
                <div style={{ marginTop: 8 }}>
                  <SkeletonText lines={2} />
                </div>
              </div>
              <Skeleton width={30} height={30} radius="8px" />
              <Skeleton width={30} height={30} radius="8px" />
            </PromptRow>
          ))}
        </PromptList>
      ) : prompts.length === 0 ? (
        <EmptyBlock>
          <EmptyIcon>
            <Spark size={20} />
          </EmptyIcon>
          <EmptyTitle>No prompts saved yet</EmptyTitle>
          <EmptySub>
            Create reusable AI prompts for common outreach scenarios.
          </EmptySub>
          <PrimaryBtn onClick={startNew} data-tour="prompt-new">
            <FiPlus size={12} /> Create your first prompt
          </PrimaryBtn>
        </EmptyBlock>
      ) : (
        <FadeIn>
          <PromptList data-tour="prompt-list">
            {prompts.map((p) => (
              <PromptRow key={p.id}>
                <PromptIcon>
                  <Spark size={14} />
                </PromptIcon>
                <div style={{ minWidth: 0 }}>
                  <PromptName>
                    {p.name}
                    {p.isDefault && <SigTag>Default</SigTag>}
                  </PromptName>
                  <PromptBody>{p.body}</PromptBody>
                </div>
                <IconBtn
                  onClick={() => toggleDefault(p.id)}
                  title={
                    p.isDefault
                      ? "Stop applying this by default"
                      : "Apply this prompt by default"
                  }
                  disabled={saving}
                  style={p.isDefault ? { color: "var(--success)" } : undefined}
                >
                  <FiStar size={14} />
                </IconBtn>
                <IconBtn onClick={() => startEdit(p)} title="Edit">
                  <FiEdit2 size={14} />
                </IconBtn>
                <IconBtn
                  $danger
                  onClick={() => remove(p.id)}
                  title="Delete"
                  disabled={saving}
                >
                  <FiTrash2 size={14} />
                </IconBtn>
              </PromptRow>
            ))}
          </PromptList>
          <div style={{ marginTop: 12 }}>
            <Btn onClick={startNew} data-tour="prompt-new">
              <FiPlus size={12} /> New prompt
            </Btn>
          </div>
        </FadeIn>
      )}
      {editorModal}
    </SettingsCard>
  );
}

// ─── Rich text editor (TipTap) ───────────────────────────────
const EditorShell = styled.div`
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const EditorToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  flex-wrap: wrap;
  /* The phone-wide 44px control min-height stretched every tool to 30×44 and the wrapped
     toolbar ate a third of the editor; keep the tools compact. */
  @media (max-width: 450px) {
    button,
    select,
    input {
      min-height: 32px;
    }
    button {
      width: 32px;
      height: 32px;
    }
  }
`;

const ToolbarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: ${({ $active, theme }) =>
    $active ? "var(--accent-soft)" : "transparent"};
  color: ${({ $active, theme }) => ($active ? theme.accent : theme.ink)};
  cursor: pointer;
  font: inherit;
  &:hover {
    background: ${({ theme }) =>
      "color-mix(in srgb, var(--ink) 5%, transparent)"};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ToolbarSep = styled.span`
  width: 1px;
  height: 18px;
  background: ${({ theme }) => theme.line};
  margin: 0 4px;
`;

const EditorBody = styled.div`
  padding: 12px 14px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.ink};
  font-size: 13.5px;
  line-height: 1.55;
  .ProseMirror {
    outline: none;
    min-height: 140px;
  }
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
  .ProseMirror p {
    margin: 0;
  }
  .ProseMirror a {
    color: ${({ theme }) => theme.accent};
    text-decoration: underline;
  }
  .ProseMirror table {
    border-collapse: collapse;
    max-width: 100%;
  }
  .ProseMirror td,
  .ProseMirror th {
    vertical-align: top;
    min-width: 1em;
    outline: 1px dashed
      ${({ theme }) => `color-mix(in srgb, ${theme.line} 70%, transparent)`};
  }
  .ProseMirror td > p:last-child,
  .ProseMirror th > p:last-child {
    margin-bottom: 0;
  }
  .ProseMirror .selectedCell {
    background: var(--accent-soft);
  }
  .ProseMirror .ProseMirror-selectednode img {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
  .ProseMirror [data-resize-handle] {
    width: 10px;
    height: 10px;
    background: ${({ theme }) => theme.accent};
    border: 1.5px solid ${({ theme }) => theme.surface};
    border-radius: 2px;
    opacity: 0;
    z-index: 2;
  }
  .ProseMirror .ProseMirror-selectednode [data-resize-handle] {
    opacity: 1;
  }
  .ProseMirror [data-resize-handle="top-left"],
  .ProseMirror [data-resize-handle="bottom-right"] {
    cursor: nwse-resize;
  }
  .ProseMirror [data-resize-handle="top-right"],
  .ProseMirror [data-resize-handle="bottom-left"] {
    cursor: nesw-resize;
  }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: ${({ theme }) => theme.inkMuted};
    opacity: 0.5;
    float: left;
    pointer-events: none;
    height: 0;
  }
`;

const SelectInput = styled.select`
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 12.5px; /* must follow the font shorthand, which resets size */
  cursor: pointer;
  outline: none;
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
`;

const ColorInput = styled.input.attrs({ type: "color" })`
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }
  &::-webkit-color-swatch {
    border-radius: 4px;
    border: 0;
  }
`;

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Inter", value: "Inter, system-ui, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times", value: '"Times New Roman", Times, serif' },
  { label: "Courier", value: '"Courier New", Courier, monospace' },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
];

// Preset sizes offered in the font-size box; any number can also be typed.
const FONT_SIZE_PRESETS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

// Font-size box: type any size (px, or pt/em as typed) or pick a preset, like Outlook.
const FontSizeBox = styled.input`
  width: 62px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.ink};
  font: inherit;
  font-size: 12.5px;
  outline: none;
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.accent};
  }
`;

// "14" → "14px"; "11pt" → "11pt"; anything else → "" (unset).
function normalizeFontSize(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return "";
  if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`;
  if (/^\d+(\.\d+)?(px|pt|em|rem|%)$/.test(v)) return v;
  return "";
}

function FontSizeInput({ value, onCommit }) {
  const [text, setText] = React.useState(value || "");
  React.useEffect(() => setText(value || ""), [value]);
  function commit() {
    onCommit(normalizeFontSize(text));
  }
  return (
    <>
      <FontSizeBox
        list="sig-font-sizes"
        value={text.replace(/px$/, "")}
        placeholder="Size"
        title="Font size (type a number or pick one)"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
      />
      <datalist id="sig-font-sizes">
        {FONT_SIZE_PRESETS.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
    </>
  );
}

// Inline-style properties worth keeping from pasted Outlook/Gmail markup.
const KEEP_STYLE_PROPS = new Set([
  "font-family", "font-size", "font-weight", "font-style", "color",
  "background", "background-color", "text-align", "vertical-align",
  "line-height", "letter-spacing", "text-decoration", "margin", "margin-top",
  "margin-bottom", "margin-left", "margin-right", "padding", "padding-top",
  "padding-bottom", "padding-left", "padding-right", "border", "border-top",
  "border-bottom", "border-left", "border-right", "border-collapse", "width",
  "height", "max-width", "white-space", "display",
]);

// Drop mso-* and other junk declarations, keep the visual ones.
function cleanInlineStyle(style) {
  return String(style || "")
    .split(";")
    .map((d) => d.trim())
    .filter((d) => {
      const prop = d.split(":")[0]?.trim().toLowerCase();
      return prop && KEEP_STYLE_PROPS.has(prop);
    })
    .join("; ");
}

// Style attribute kept on a pasted element, or null when nothing survives.
function parseStyleAttr(element) {
  const cleaned = cleanInlineStyle(element.getAttribute("style"));
  return cleaned || null;
}

// Pasted Outlook signatures are table layouts with inline styles. Tiptap
// keeps only the attributes it knows, so this carries the layout ones through.
const OutlookStyles = Extension.create({
  name: "outlookStyles",
  addGlobalAttributes() {
    const styleAttr = {
      style: {
        default: null,
        parseHTML: parseStyleAttr,
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
    const plainAttr = (name) => ({
      default: null,
      parseHTML: (el) => el.getAttribute(name),
      renderHTML: (attrs) => (attrs[name] != null ? { [name]: attrs[name] } : {}),
    });
    return [
      { types: ["paragraph", "tableRow"], attributes: styleAttr },
      {
        types: ["table"],
        attributes: {
          ...styleAttr,
          width: plainAttr("width"),
          border: plainAttr("border"),
          cellpadding: plainAttr("cellpadding"),
          cellspacing: plainAttr("cellspacing"),
          align: plainAttr("align"),
        },
      },
      {
        types: ["tableCell", "tableHeader"],
        attributes: {
          ...styleAttr,
          width: plainAttr("width"),
          height: plainAttr("height"),
          valign: plainAttr("valign"),
          bgcolor: plainAttr("bgcolor"),
        },
      },
    ];
  },
});

// Legacy <font size=1..7> → px, for older Outlook/Gmail markup.
const FONT_TAG_SIZES = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 };

// Clean Word/Outlook clipboard HTML before ProseMirror parses it.
function transformPastedHTML(html) {
  let out = String(html || "");
  out = out.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "");
  out = out.replace(/<!--[\s\S]*?-->/g, "");
  out = out.replace(/<(style|script|xml|meta|link|title)\b[^>]*>[\s\S]*?<\/\1>/gi, "");
  out = out.replace(/<(meta|link)\b[^>]*\/?>/gi, "");
  out = out.replace(/<\/?[ovwm]:[^>]*>/gi, "");
  // <font face size color> → <span style="...">
  out = out.replace(/<font\b([^>]*)>/gi, (_m, attrs) => {
    const get = (n) => (attrs.match(new RegExp(`${n}\\s*=\\s*["']?([^"'\\s>]+)`, "i")) || [])[1];
    const decl = [];
    const face = get("face");
    const size = get("size");
    const color = get("color");
    if (face) decl.push(`font-family: ${face}`);
    if (size && FONT_TAG_SIZES[size]) decl.push(`font-size: ${FONT_TAG_SIZES[size]}px`);
    if (color) decl.push(`color: ${color}`);
    return decl.length ? `<span style="${decl.join("; ")}">` : "<span>";
  });
  out = out.replace(/<\/font>/gi, "</span>");
  // Images the browser can't fetch (Outlook desktop cid:/file: refs) are dropped.
  out = out.replace(/<img\b[^>]*src\s*=\s*["']?(cid:|file:)[^>]*>/gi, "");
  return out;
}

// True when the clipboard carries Outlook-desktop image refs we can't import.
function hasUnreachableImages(html) {
  return /<img\b[^>]*src\s*=\s*["']?(cid:|file:)/i.test(String(html || ""));
}

// Read a File into a data: URL. Caps the raw file at SIG_IMAGE_MAX bytes.
const SIG_IMAGE_MAX = 200 * 1024; // 200KB before base64

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (file.size > SIG_IMAGE_MAX) {
      reject(
        new Error(
          `Image is too large (${Math.round(
            file.size / 1024,
          )}KB). Max is ${SIG_IMAGE_MAX / 1024}KB.`,
        ),
      );
      return;
    }
    if (!/^image\//.test(file.type)) {
      reject(new Error("Only image files are allowed."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}

function SignatureEditor({ value, onChange }) {
  const fileInputRef = React.useRef(null);
  const { showToast } = useToast();

  const editor = useEditor({
    extensions: [
      // StarterKit already bundles Link and Underline.
      StarterKit.configure({
        link: { openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } },
        // Tight paragraphs like Outlook: one Enter = one line in the email too.
        paragraph: { HTMLAttributes: { style: "margin: 0" } },
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      FontFamily.configure({ types: ["textStyle"] }),
      FontSize,
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      // Inline so logos sit beside text like Outlook; corner handles resize.
      Image.configure({
        inline: true,
        allowBase64: true,
        resize: { enabled: true, minWidth: 16, minHeight: 16, alwaysPreserveAspectRatio: true },
      }),
      TableKit.configure({ table: { resizable: false } }),
      OutlookStyles,
    ],
    content: value || "",
    editorProps: {
      transformPastedHTML,
      // Image files on the clipboard (copied picture) become inline data: images.
      handlePaste: (view, event) => {
        const cd = event.clipboardData;
        if (!cd) return false;
        if (hasUnreachableImages(cd.getData("text/html"))) {
          showToast("Pictures copied from the Outlook desktop app can't be transferred. Add them with the image button.", { duration: 6000 });
        }
        const files = Array.from(cd.files || []).filter((f) => /^image\//.test(f.type));
        if (!files.length || cd.getData("text/html")) return false;
        Promise.all(files.map(fileToDataUrl))
          .then((urls) => {
            const { schema } = view.state;
            urls.forEach((src) => {
              const node = schema.nodes.image.create({ src });
              view.dispatch(view.state.tr.replaceSelectionWith(node).scrollIntoView());
            });
          })
          .catch((err) => showToast(err.message || "Could not add image"));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  React.useEffect(() => {
    // Sync external value resets (e.g., switching between editing signatures).
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [editor, value === "" ? "" : null]);

  if (!editor) return null;

  async function handlePickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      showToast(err.message || "Could not add image");
    }
  }

  function promptLink() {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }

  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "";
  const currentFontSize = editor.getAttributes("textStyle").fontSize || "";
  const currentColor = editor.getAttributes("textStyle").color || light.ink;

  function setFontFamily(value) {
    if (!value) {
      editor.chain().focus().unsetFontFamily().run();
    } else {
      editor.chain().focus().setFontFamily(value).run();
    }
  }
  function setFontSize(value) {
    if (!value) {
      editor.chain().focus().unsetFontSize().run();
    } else {
      editor.chain().focus().setFontSize(value).run();
    }
  }
  function setColor(value) {
    editor.chain().focus().setColor(value).run();
  }

  return (
    <EditorShell>
      <EditorToolbar>
        <SelectInput
          value={currentFontFamily}
          onChange={(e) => setFontFamily(e.target.value)}
          title="Font family"
          style={{ minWidth: 110 }}
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.label} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectInput>
        <FontSizeInput value={currentFontSize} onCommit={setFontSize} />
        <ToolbarSep />
        <ToolbarBtn
          $active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <FiBold size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          $active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <FiItalic size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          $active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <FiUnderline size={14} />
        </ToolbarBtn>
        <ColorInput
          value={currentColor}
          onChange={(e) => setColor(e.target.value)}
          title="Text color"
        />
        <ToolbarSep />
        <ToolbarBtn
          $active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Align left"
        >
          <FiAlignLeft size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          $active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Align center"
        >
          <FiAlignLeft size={14} style={{ transform: "scaleX(-1)" }} />
        </ToolbarBtn>
        <ToolbarBtn
          $active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Align right"
        >
          <FiAlignLeft size={14} style={{ transform: "rotate(180deg)" }} />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn
          $active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <FiList size={14} />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn
          $active={editor.isActive("link")}
          onClick={promptLink}
          title="Link"
        >
          <FiLink size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => fileInputRef.current?.click()}
          title="Insert image"
        >
          <FiImage size={14} />
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handlePickImage}
        />
      </EditorToolbar>
      <EditorBody>
        <EditorContent editor={editor} />
      </EditorBody>
    </EditorShell>
  );
}

// HTML sanitization config used for both save AND render. Allows img with
// src, alt, width, height (data: URLs included so signature images survive
// the round-trip — they're swapped to cid: at send time).
const SIG_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "div", "span", "strong", "b", "em", "i", "u", "s", "strike",
    "ul", "ol", "li", "a", "img", "h1", "h2", "h3", "h4", "blockquote",
    "code", "pre", "table", "thead", "tbody", "tfoot", "tr", "td", "th",
    "colgroup", "col",
  ],
  ALLOWED_ATTR: [
    "href", "src", "alt", "title", "rel", "target", "width", "height", "style",
    "colspan", "rowspan", "cellpadding", "cellspacing", "border", "align",
    "valign", "bgcolor",
  ],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|data:image\/(png|jpeg|jpg|gif|webp);)/i,
  // Plain values like width="120" fail the URI regexp above unless marked URI-safe.
  ADD_URI_SAFE_ATTR: [
    "width", "height", "colspan", "rowspan", "cellpadding", "cellspacing",
    "border", "align", "valign", "bgcolor",
  ],
};

function sanitizeSignatureHtml(html) {
  // Empty paragraphs collapse to nothing in mail clients; keep them as one blank line.
  const kept = String(html || "").replace(/<p([^>]*)>(?:\s|&nbsp;)*<\/p>/gi, "<p$1><br></p>");
  return DOMPurify.sanitize(kept, SIG_SANITIZE_CONFIG);
}

// ─── Signatures panel ─────────────────────────────────────────
function SignaturesPanel() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sigs, setSigs] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [draft, setDraft] = React.useState({ name: "", body: "" });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!user?.sub) return;
    let mounted = true;
    getUserSettings(user.sub)
      .then((doc) => {
        if (!mounted) return;
        const arr = Array.isArray(doc?.signatures) ? doc.signatures : [];
        setSigs(arr);
        setLoaded(true);
      })
      .catch(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, [user?.sub]);

  async function persist(next) {
    if (!user?.sub) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(
        `/api/user-settings/${encodeURIComponent(user.sub)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ signatures: next }),
        },
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Save failed");
      }
      invalidateUserSettingsCache(user.sub);
      setSigs(next);
    } catch (err) {
      showToast(err.message || "Couldn't save signature");
      throw err;
    } finally {
      setSaving(false);
    }
  }

  function startNew() {
    setEditing("new");
    setDraft({ name: "", body: "" });
  }
  function startEdit(s) {
    setEditing(s.id);
    setDraft({ name: s.name, body: s.body });
  }
  function cancel() {
    setEditing(null);
  }
  async function save() {
    if (!draft.name.trim()) return;
    const cleanBody = sanitizeSignatureHtml(draft.body);
    let next;
    if (editing === "new") {
      next = [
        ...sigs,
        {
          id: `s${Date.now()}`,
          name: draft.name.trim(),
          body: cleanBody,
          isDefault: sigs.length === 0,
        },
      ];
    } else {
      next = sigs.map((s) =>
        s.id === editing
          ? { ...s, name: draft.name.trim(), body: cleanBody }
          : s,
      );
    }
    try {
      await persist(next);
      setEditing(null);
      showToast("Signature saved");
    } catch (_) {
      // toast already shown
    }
  }
  async function makeDefault(id) {
    try {
      await persist(sigs.map((s) => ({ ...s, isDefault: s.id === id })));
    } catch (_) {}
  }
  async function remove(id) {
    try {
      await persist(sigs.filter((s) => s.id !== id));
    } catch (_) {}
  }

  // Close the editor modal on Escape.
  React.useEffect(() => {
    if (editing === null) return;
    function onKey(e) {
      if (e.key === "Escape" && !saving) cancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, saving]);

  const editorModal =
    editing === null
      ? null
      : createPortal(
          <ModalOverlay onMouseDown={() => !saving && cancel()}>
            <PageScrollLock />
            <ModalCard onMouseDown={(e) => e.stopPropagation()}>
              <EditTitle>
                {editing === "new" ? "Create a new signature" : "Edit signature"}
              </EditTitle>
              <Field data-tour="sig-name">
                <label>Signature name</label>
                <TextInput
                  placeholder="e.g. Default, Tech roles, Asia outreach"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  style={{ maxWidth: 400 }}
                />
              </Field>
              <Field
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 300,
                  marginBottom: 0,
                }}
                data-tour="sig-content"
              >
                <label>Signature content</label>
                <SignatureEditor
                  value={draft.body}
                  onChange={(html) => setDraft((d) => ({ ...d, body: html }))}
                />
                <FieldHint>
                  Rich text + images. Images are embedded inline (max 200KB each)
                  and convert to a proper email attachment when sent.
                </FieldHint>
              </Field>
              <EditFoot>
                <Btn onClick={cancel} disabled={saving} data-tour="sig-cancel">
                  Cancel
                </Btn>
                <PrimaryBtn
                  onClick={save}
                  disabled={saving || !draft.name.trim()}
                  data-tour="sig-save"
                >
                  {saving ? (
                    <>
                      <Spinner /> Saving…
                    </>
                  ) : editing === "new" ? (
                    "Create signature"
                  ) : (
                    "Save changes"
                  )}
                </PrimaryBtn>
              </EditFoot>
            </ModalCard>
          </ModalOverlay>,
          document.body,
        );

  return (
    <SettingsCard
      title="Signatures"
      sub="Email signatures appended to outreach. Set a default and switch in the composer when sending."
    >
      {!loaded ? (
        // Skeleton signature cards matching the SigCard/SigHead row layout
        // (13px padding, 14px name, right-aligned actions) so the list region
        // holds its space until the real signatures fade in.
        <SigList>
          {Array.from({ length: 3 }).map((_, i) => (
            <SigCard key={i} as="div" style={{ cursor: "default" }}>
              <SigHead>
                <Skeleton width={140 + ((i * 40) % 80)} height={14} />
                <div style={{ flex: 1 }} />
                <Skeleton width={30} height={30} radius="8px" />
              </SigHead>
            </SigCard>
          ))}
        </SigList>
      ) : sigs.length === 0 ? (
        <EmptyBlock>
          <EmptyIcon>
            <FiEdit2 size={20} />
          </EmptyIcon>
          <EmptyTitle>No signatures saved yet</EmptyTitle>
          <EmptySub>
            Save signatures to append automatically to outreach emails.
          </EmptySub>
          <PrimaryBtn onClick={startNew} data-tour="sig-new">
            <FiPlus size={12} /> Create your first signature
          </PrimaryBtn>
        </EmptyBlock>
      ) : (
        <FadeIn>
          <SigList data-tour="sig-list">
            {sigs.map((s) => (
              <SigCard key={s.id} onClick={() => startEdit(s)} title="Edit signature">
                <SigHead>
                  <SigName>
                    {s.name}
                    {s.isDefault && <SigTag>Default</SigTag>}
                  </SigName>
                  <div style={{ flex: 1 }} />
                  {!s.isDefault && (
                    <Btn
                      $sm
                      onClick={(e) => {
                        e.stopPropagation();
                        makeDefault(s.id);
                      }}
                      disabled={saving}
                    >
                      <FiStar size={12} /> Set as default
                    </Btn>
                  )}
                  <IconBtn
                    $danger
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(s.id);
                    }}
                    title="Delete"
                    disabled={saving}
                  >
                    <FiTrash2 size={14} />
                  </IconBtn>
                </SigHead>
              </SigCard>
            ))}
          </SigList>
          <div style={{ marginTop: 12 }}>
            <Btn onClick={startNew} data-tour="sig-new">
              <FiPlus size={12} /> New signature
            </Btn>
          </div>
        </FadeIn>
      )}
      {editorModal}
    </SettingsCard>
  );
}

// Copy for the tabs that need Outreach, shown in place of the panel when the plan lacks it.
const TRIAL_NOTE = "The free trial covers finding candidates; pick a plan to switch this on.";
const OUTREACH_TAB_LOCKS = {
  mailbox: {
    sub: "Send outreach from your own Gmail or Outlook address.",
    title: "Mailbox connection is part of the Starter plan and up",
    text: `Connect Gmail or Outlook so emails to candidates go out from your own address. ${TRIAL_NOTE}`,
  },
  prompts: {
    sub: "How the AI writes your outreach emails.",
    title: "AI prompts are part of the Starter plan and up",
    text: `Save how you like your outreach written, and the AI follows it for every email. ${TRIAL_NOTE}`,
  },
  signatures: {
    sub: "Your sign-off at the bottom of every email.",
    title: "Signatures are part of the Starter plan and up",
    text: `Set your signature once and it goes at the bottom of every email you send. ${TRIAL_NOTE}`,
  },
};

// ─── App ─────────────────────────────────────────────────────
export default function Settings() {
  // `/settings?tab=credits` deep-links a panel (the sidebar credits chip uses it).
  const { search, key: navKey } = useLocation();
  const wantedTab = React.useMemo(() => {
    const wanted = new URLSearchParams(search).get("tab");
    return NAV.some((n) => n.id === wanted) ? wanted : null;
  }, [search]);
  const [active, setActive] = React.useState(() => wantedTab || "profile");
  const { features: planFeatures } = usePlan();
  // Parked: the Team tab only shows once FEATURE_TEAM_SEATS is on.
  const { features: appFeatures } = useAuth();
  const teamOn = Boolean(appFeatures?.teamSeats);
  // Follow the URL on every navigation, not just when it changes: clicking the sidebar chip
  // while the query is already ?tab=credits (after browsing other sections) must still jump.
  React.useEffect(() => {
    if (wantedTab) setActive(wantedTab);
  }, [wantedTab, navKey]);

  // Keep the chosen section visible in the stacked tab strip (deep links can land off-screen).
  const activeItemRef = React.useRef(null);
  React.useEffect(() => {
    activeItemRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [active]);

  // Refer a friend shows for paid users (their link) and anyone who signed up with a referral (their
  // progress, or how it fell through). Hidden until we know, and for everyone else.
  const [showReferrals, setShowReferrals] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/referrals")
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!alive) return;
        const show = Boolean(body && (body.eligible || body.referredBy));
        setShowReferrals(show);
        // A deep link to the hidden tab lands on Profile instead of an empty page.
        if (!show) setActive((cur) => (cur === "referrals" ? "profile" : cur));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const grouped = React.useMemo(() => {
    return NAV.filter((item) => (item.id !== "team" || teamOn) && (item.id !== "referrals" || showReferrals)).reduce((acc, item) => {
      (acc[item.group] = acc[item.group] || []).push(item);
      return acc;
    }, {});
  }, [teamOn, showReferrals]);

  // The plan's locked tabs (the free trial has no Outreach, so no mailbox, prompts or signatures).
  const tabLocked = (it) => planFeatures[it.feature] === false;
  const activeItem = NAV.find((n) => n.id === active);
  const outreachLock = activeItem && tabLocked(activeItem) ? OUTREACH_TAB_LOCKS[active] : null;

  let panel = null;
  // A team link with team seats off shows Profile, not the parked Team panel.
  if (outreachLock)
    panel = (
      <SettingsCard title={activeItem.label} sub={outreachLock.sub}>
        <PlanLockPanel plan="Starter" title={outreachLock.title} text={outreachLock.text} />
      </SettingsCard>
    );
  else if (active === "profile" || (active === "team" && !teamOn)) panel = <ProfilePanel />;
  else if (active === "team") panel = <TeamPanel />;
  else if (active === "appearance") panel = <AppearancePanel />;
  else if (active === "mailbox") panel = <MailboxPanel />;
  else if (active === "credits") panel = <CreditsPanel />;
  else if (active === "referrals") panel = <ReferralsPanel />;
  else if (active === "billing") panel = <BillingPanel />;
  else if (active === "email-list")
    panel = !planFeatures.emailAlerts ? (
      <SettingsCard title="Email alerts" sub="A daily email of new roles at the companies you follow.">
        <PlanLockPanel
          plan="Growth"
          title="Email alerts are part of the Growth plan"
          text="Move to Growth to pick the companies you want alerts for. The change applies straight away."
        />
      </SettingsCard>
    ) : (
      <ChecklistPanel
        title="Email alerts"
        sub="Each morning, one email with the roles posted since the last one at the companies you tick here."
        field="companies"
        extras={<JobAlertControls />}
      />
    );
  else if (active === "prompts") panel = <PromptsPanel />;
  else if (active === "signatures") panel = <SignaturesPanel />;

  return (
    <Bleed>
      <Shell>
        <Rail>
          <RailHeader>
            <h1>Settings</h1>
            <div>Manage workspace, sourcing, and email preferences.</div>
          </RailHeader>
          <RailNav aria-label="Settings sections">
            {Object.entries(grouped).map(([group, items]) => (
              <RailGroup key={group}>
                <RailGroupHeader>{group}</RailGroupHeader>
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <RailItem
                      key={it.id}
                      ref={active === it.id ? activeItemRef : null}
                      $active={active === it.id}
                      aria-current={active === it.id ? "page" : undefined}
                      onClick={() => setActive(it.id)}
                    >
                      <Icon size={16} />
                      {it.label}
                      {tabLocked(it) && (
                        <FiLock size={12} aria-label="Not on your plan" style={{ marginLeft: "auto", color: "var(--secondary)" }} />
                      )}
                    </RailItem>
                  );
                })}
              </RailGroup>
            ))}
          </RailNav>
        </Rail>
        <Pane>
          <PaneScale>{panel}</PaneScale>
        </Pane>
      </Shell>
    </Bleed>
  );
}
